/*
 * pl-wishlist — お気に入り（Wishlist）クライアント
 * Phase 1.2 / Sprint 1: 追加・削除・トースト・楽観的更新・バリアント追従
 *
 * 構成:
 *   - PlWishlistClient : Core（API ファサード）
 *   - WishlistAPI       : 状態管理（LocalStorage ＋ ログイン顧客は Metafields 同期）
 *   - LocalWishlistStorage : ゲスト用 LocalStorage（key: pl_wishlist:wishlist / schemaVersion 2）
 *   - ToastUI           : トースト通知
 *   - PlWishlistButton  : <pl-wishlist-button> カスタム要素（card / pdp モード）
 *
 * 設定は window.plWishlistConfig（theme.liquid から注入）:
 *   { apiBase: '/apps/pl-wishlist', customerId: 'gid://shopify/Customer/123' | null }
 */
(function () {
  'use strict';

  if (window.plWishlist) return; // 二重初期化ガード

  var VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/';
  var PRODUCT_GID_PREFIX = 'gid://shopify/Product/';
  var STORAGE_KEY = 'pl_wishlist:wishlist';
  var SCHEMA_VERSION = 2;
  var TOAST_DURATION_MS = 3200;
  var WISHLIST_CHANGE_EVENT = 'pl-wishlist:change';
  // Sprint 6（D1）: PDP の廃盤×在庫の状態変化を緊急性/終了メッセージへ伝える内部イベント。
  var VARIANT_STATE_EVENT = 'pl-wishlist:variant-state';
  // カート更新時に差し替える Dawn のセクション（スティッキーカートと同じ手法）。
  var CART_SECTION_IDS = ['cart-icon-bubble', 'cart-notification', 'cart-drawer'];
  // ログイン直後の自動マージを「1 顧客につき 1 セッション 1 回」に抑えるための sessionStorage マーカー接頭辞。
  var MERGE_MARKER_PREFIX = 'pl_wishlist:merged_for:';

  // ------------------------------------------------------------------
  // LocalStorage（ゲスト用）
  // ------------------------------------------------------------------
  function LocalWishlistStorage() {}

  LocalWishlistStorage.prototype.load = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var data = JSON.parse(raw);

      // v1 (productIds) → v2 (variantIds) のマイグレーション: v1 は破棄
      if (data && data.schemaVersion === 1) {
        localStorage.removeItem(STORAGE_KEY);
        return [];
      }
      if (!data || !Array.isArray(data.variantIds)) return [];

      return data.variantIds.filter(function (v) {
        return typeof v === 'string' && v.indexOf(VARIANT_GID_PREFIX) === 0;
      });
    } catch (e) {
      return [];
    }
  };

  LocalWishlistStorage.prototype.save = function (variantIds) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          variantIds: variantIds,
          updatedAt: new Date().toISOString(),
          schemaVersion: SCHEMA_VERSION,
        })
      );
    } catch (e) {
      // QuotaExceeded 等。お気に入りは軽量なので通常起きないが握り潰さずログ。
      console.warn('[pl-wishlist] localStorage save failed:', e);
    }
  };

  // ------------------------------------------------------------------
  // 生産終了メタ（Sprint 7 / F-303b・F-303f・データ要件 §5.2）
  //   一覧の 60 日カウントダウンの起点（discontinued_at）を「このブラウザが当該バリアントを
  //   初めて生産終了と認識した日」としてローカル保持する。お気に入り本体（variantIds の Set・
  //   STORAGE_KEY）とは **別キー** に持つことで、既存のお気に入り（ID 配列）を一切壊さない
  //   （AC-11・schema 移行不要）。
  //   - ログイン顧客: サーバの真実（wishlist_index.discontinued_at・Cron が実削除）とは別に、
  //     一覧表示の残日数算出にこのローカル値を使う（見え方をゲストと揃える・design-v5 §W1-7）。
  //   - 生産終了が解除（復活）された行はキーを掃除し、起点を持ち越さない。
  //   形式: { [variantGid]: ISO8601文字列(初認識日), ... }
  // ------------------------------------------------------------------
  var DISC_META_KEY = 'pl_wishlist:discontinued';

  function DiscontinuedMetaStore() {
    this._cache = null;
  }

  /** ローカルの生産終了メタ（variantGid → 初認識 ISO 文字列）を読む。壊れていれば {}。 */
  DiscontinuedMetaStore.prototype.load = function () {
    if (this._cache) return this._cache;
    var map = {};
    try {
      var raw = localStorage.getItem(DISC_META_KEY);
      if (raw) {
        var data = JSON.parse(raw);
        if (data && typeof data === 'object' && data.map && typeof data.map === 'object') {
          map = data.map;
        }
      }
    } catch (e) {
      map = {};
    }
    this._cache = map;
    return map;
  };

  DiscontinuedMetaStore.prototype._save = function (map) {
    this._cache = map;
    try {
      localStorage.setItem(DISC_META_KEY, JSON.stringify({ map: map, schemaVersion: 1 }));
    } catch (e) {
      // 失敗しても致命的でない（カウントダウンは「まもなく削除」フォールバックで破綻しない）。
    }
  };

  /**
   * 当該バリアントの生産終了起点（ISO 文字列）を返す。未記録なら今を起点として記録して返す
   * （＝このブラウザが初めて生産終了と認識した日・spec §5.2）。
   */
  DiscontinuedMetaStore.prototype.getOrInit = function (variantGid) {
    var map = this.load();
    if (map[variantGid]) return map[variantGid];
    var now = new Date().toISOString();
    map[variantGid] = now;
    this._save(map);
    return now;
  };

  /** 生産終了が解除（復活）/削除されたバリアントの起点を掃除する（持ち越さない）。 */
  DiscontinuedMetaStore.prototype.forget = function (variantGid) {
    var map = this.load();
    if (map[variantGid]) {
      delete map[variantGid];
      this._save(map);
    }
  };

  /**
   * 現在「生産終了として認識中」の variantGid 集合だけを残し、それ以外の起点を掃除する。
   * 一覧描画後に「もう生産終了でなくなった/お気に入りから消えた」行の起点を持ち越さないため。
   */
  DiscontinuedMetaStore.prototype.retainOnly = function (activeGidSet) {
    var map = this.load();
    var changed = false;
    for (var gid in map) {
      if (Object.prototype.hasOwnProperty.call(map, gid) && !activeGidSet.has(gid)) {
        delete map[gid];
        changed = true;
      }
    }
    if (changed) this._save(map);
  };

  // 60 日カウントダウン（固定・spec / 01_requirements DC-020）。
  var COUNTDOWN_TOTAL_DAYS = 60;
  // 期限間近（soon）に入るしきい値（残り 7 日以内・design-v5 §W1-3）。
  var COUNTDOWN_SOON_DAYS = 7;
  var COUNTDOWN_DAY_MS = 24 * 60 * 60 * 1000;

  /**
   * 起点（discontinued_at の ISO）と現在時刻から残日数・段階を算出する（描画時1回・新規 fetch なし）。
   * @returns { daysLeft:number(>=0), stage:'normal'|'soon'|'due', text:string }
   */
  function computeCountdown(discontinuedAtIso) {
    var startMs = Date.parse(discontinuedAtIso);
    if (isNaN(startMs)) {
      // 起点不明: 静かな終了文にフォールバック（破綻させない）。due 本文と表記を揃える（G-4・「一覧から」）。
      return { daysLeft: 0, stage: 'due', text: 'まもなく一覧から削除されます' };
    }
    var elapsedDays = Math.floor((Date.now() - startMs) / COUNTDOWN_DAY_MS);
    var daysLeft = COUNTDOWN_TOTAL_DAYS - elapsedDays;
    if (daysLeft <= 0) {
      return { daysLeft: 0, stage: 'due', text: 'まもなく一覧から削除されます' };
    }
    if (daysLeft <= COUNTDOWN_SOON_DAYS) {
      return {
        daysLeft: daysLeft,
        // G-4: normal と「自動的に」の有無で語が割れていたので soon にも補完して統一。
        stage: 'soon',
        text: 'まもなく — あと ' + daysLeft + ' 日で一覧から自動的に削除されます',
      };
    }
    return {
      daysLeft: daysLeft,
      stage: 'normal',
      text: 'あと ' + daysLeft + ' 日で一覧から自動的に削除されます',
    };
  }

  // ------------------------------------------------------------------
  // Toast
  // ------------------------------------------------------------------
  function ToastUI() {
    this.container = null;
    this.timer = null;
  }

  ToastUI.prototype.ensureContainer = function () {
    if (this.container && document.body.contains(this.container)) return this.container;
    var el = document.getElementById('pl-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pl-toast';
      el.className = 'pl-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      document.body.appendChild(el);
    }
    this.container = el;
    return el;
  };

  ToastUI.prototype.show = function (message, variant) {
    var el = this.ensureContainer();
    // 通常トースト: textContent でプレーンなテキストに戻す（持続トースト＝restock opt-in の
    // 内部 DOM があれば一掃される）。className も plain に戻すため pl-toast--optin が消える。
    el.textContent = message;
    // variant: 'error' | 'success' | 'info'（既定）。success/info は装飾の差のみ（既存 navy 地）。
    var cls = 'pl-toast pl-toast--visible';
    if (variant === 'error') cls += ' pl-toast--error';
    else if (variant === 'success') cls += ' pl-toast--success';
    el.className = cls;

    if (this.timer) clearTimeout(this.timer);
    var self = this;
    this.timer = setTimeout(function () {
      el.className = 'pl-toast';
    }, TOAST_DURATION_MS);
  };

  /**
   * Sprint8 X1（F-401・案A）: 同時購読提案つきの「持続トースト」を出す。
   * お気に入り追加成功トーストに「再入荷通知も受け取る」チェックボックス（既定 ON）を差し込み、
   * P1（在庫切れ×非生産終了×未購読）のときだけ使う。**自動消滅させない**（チェック操作の時間を確保）。
   * 手動の × で閉じる／閉じる時に checked なら opts.onConfirm(true) を呼ぶ（購読を実行）。
   *
   * @param {string} message  本文（例: お気に入りに追加しました）
   * @param {Object} opts
   *   - labelText {string}   チェックボックスのラベル（たたき台「再入荷通知も受け取る」）
   *   - defaultChecked {boolean} 既定 ON（true）
   *   - onConfirm {function(checked:boolean)} 閉じる時に最終 checked 状態で1回呼ぶ
   */
  ToastUI.prototype.showRestockOptin = function (message, opts) {
    opts = opts || {};
    var el = this.ensureContainer();
    var self = this;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // 既存内容をクリアして DOM を作り直す（プレーン↔opt-in を行き来しても壊れない）。
    el.textContent = '';
    el.className = 'pl-toast pl-toast--visible pl-toast--optin';

    // 確定（閉じる）を一度だけ実行するためのガード。
    var confirmed = false;
    var checkbox = null;
    var keyHandler = null;
    var doConfirm = function () {
      if (confirmed) return;
      confirmed = true;
      if (keyHandler) {
        document.removeEventListener('keydown', keyHandler);
        keyHandler = null;
      }
      var checked = checkbox ? checkbox.checked : false;
      el.className = 'pl-toast';
      el.textContent = '';
      if (typeof opts.onConfirm === 'function') {
        try { opts.onConfirm(checked); } catch (e) { /* 購読側でハンドル */ }
      }
    };

    // --- 本文行（メッセージ＋閉じる）---
    var msgRow = document.createElement('div');
    msgRow.className = 'pl-toast__msg-row';
    var msg = document.createElement('span');
    msg.className = 'pl-toast__msg';
    msg.textContent = message;
    msgRow.appendChild(msg);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pl-toast__close';
    closeBtn.setAttribute('aria-label', '閉じる');
    // aria-live 領域内のため × は装飾文字だが SR には aria-label を読ませる。
    closeBtn.innerHTML = '<span aria-hidden="true">×</span>';
    closeBtn.addEventListener('click', doConfirm);
    msgRow.appendChild(closeBtn);
    el.appendChild(msgRow);

    // --- opt-in 行（チェックボックス＋ラベル）---
    var optin = document.createElement('div');
    optin.className = 'pl-toast__restock-optin';

    checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'pl-toast__optin-checkbox';
    checkbox.id = 'pl-toast-restock-optin';
    checkbox.checked = opts.defaultChecked !== false; // 既定 ON。

    var label = document.createElement('label');
    label.className = 'pl-toast__optin-label';
    label.setAttribute('for', 'pl-toast-restock-optin');
    label.textContent = opts.labelText || '再入荷通知も受け取る';

    optin.appendChild(checkbox);
    optin.appendChild(label);
    el.appendChild(optin);

    // Esc でトースト全体を確定（閉じる）。チェックボックスの Space トグルはネイティブ挙動。
    keyHandler = function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        doConfirm();
      }
    };
    document.addEventListener('keydown', keyHandler);

    // フォーカスはチェックボックスへ（キーボードで即トグル/閉じが可能）。
    try { checkbox.focus(); } catch (e) {}
  };

  // ------------------------------------------------------------------
  // WishlistAPI（状態 + 永続化）
  // ------------------------------------------------------------------
  function WishlistAPI(client) {
    this.client = client;
    this.storage = new LocalWishlistStorage();
    this.cache = new Set();
  }

  WishlistAPI.prototype.init = function () {
    var self = this;
    this.cache = new Set(this.storage.load());
    this.notifyChange();

    // ログイン顧客: リモート（Metafields）と統合してローカルにも反映
    if (this.client.customerId) {
      return this.client
        .fetch('/wishlist', { method: 'GET' })
        .then(function (remote) {
          var ids = (remote && remote.variant_ids) || [];
          ids.forEach(function (id) {
            self.cache.add(id);
          });
          self.storage.save(self.toArray());
          self.notifyChange();
        })
        .catch(function (err) {
          console.warn('[pl-wishlist] init sync failed:', err);
        });
    }
    return Promise.resolve();
  };

  /**
   * ログイン/新規登録直後に、ゲスト時の LocalStorage お気に入りを Customer Metafields へ
   * 一括マージする（F-105）。
   *
   * 設計:
   * - 「ログイン直後」を厳密に判定する手段がテーマ側にないため、sessionStorage の
   *   顧客別マーカーで「このセッションでこの顧客のマージを既に実行したか」を管理し、
   *   未実行のときだけ 1 回マージする（ページ遷移のたびに二重実行されない）。
   * - マージは非同期（fetch）で実行し、init のメインフローを待たせない＝ UI をブロックしない。
   * - LocalStorage は消さない（ログアウト後もローカルのお気に入りを保持する仕様）。
   * - merged_count > 0 のときだけ同期トーストを出す。
   */
  WishlistAPI.prototype.maybeMergeFromLocalStorage = function () {
    if (!this.client.customerId) return Promise.resolve();

    var marker = MERGE_MARKER_PREFIX + this.client.customerId;
    try {
      if (sessionStorage.getItem(marker)) return Promise.resolve(); // 既にこのセッションでマージ済み
    } catch (e) {
      // sessionStorage 不可（プライベートモード等）。マーカーが使えない場合でも下記で
      // 「マージ後にローカル件数が変わらなければトーストを出さない」ため重大な二重実行害はない。
    }

    var localIds = this.storage.load();
    if (localIds.length === 0) {
      this.setMergeMarker(marker);
      return Promise.resolve();
    }

    var self = this;
    return this.client
      .fetch('/wishlist/merge', {
        method: 'POST',
        body: JSON.stringify({ variant_ids: localIds }),
      })
      .then(function (result) {
        self.setMergeMarker(marker);
        var merged = (result && result.merged_count) || 0;

        // マージ後のリモート確定値をローカルにも反映（重複なく統合された状態に揃える）。
        var serverIds = (result && result.variant_ids) || [];
        if (serverIds.length) {
          serverIds.forEach(function (id) {
            self.cache.add(id);
          });
          self.storage.save(self.toArray());
          self.notifyChange();
        }

        if (merged > 0) {
          self.client.toast.show('お気に入り' + merged + '件を同期しました');
        }
      })
      .catch(function (err) {
        // マージ失敗時はマーカーを立てない（次の機会に再試行できる）。握り潰さずログ。
        console.warn('[pl-wishlist] merge failed:', err);
      });
  };

  WishlistAPI.prototype.setMergeMarker = function (marker) {
    try {
      sessionStorage.setItem(marker, '1');
    } catch (e) {
      // 失敗しても致命的でない。
    }
  };

  WishlistAPI.prototype.toArray = function () {
    // Set は array-like ではないため Array.prototype.slice.call は使えない（常に []）。
    // Array.from で確実に配列化する（add/remove の保存・init のローカル反映・件数同期で共用）。
    return Array.from(this.cache);
  };

  WishlistAPI.prototype.has = function (variantGid) {
    return this.cache.has(variantGid);
  };

  WishlistAPI.prototype.count = function () {
    return this.cache.size;
  };

  /** 追加（冪等）。楽観的にローカルを更新し、ログイン顧客はバックエンドにも反映。 */
  WishlistAPI.prototype.add = function (variantGid) {
    if (!variantGid || variantGid.indexOf(VARIANT_GID_PREFIX) !== 0) {
      return Promise.reject(new Error('Invalid variantGid'));
    }
    if (this.cache.has(variantGid)) {
      return Promise.resolve(); // 冪等
    }
    this.cache.add(variantGid);
    this.storage.save(this.toArray());
    this.notifyChange();

    if (this.client.customerId) {
      var self = this;
      return this.client
        .fetch('/wishlist', {
          method: 'POST',
          body: JSON.stringify({ variant_id: variantGid }),
        })
        .catch(function (err) {
          // バックエンド失敗時はロールバック
          self.cache.delete(variantGid);
          self.storage.save(self.toArray());
          self.notifyChange();
          throw err;
        });
    }
    return Promise.resolve();
  };

  /** 削除（冪等）。 */
  WishlistAPI.prototype.remove = function (variantGid) {
    if (!this.cache.has(variantGid)) {
      return Promise.resolve();
    }
    this.cache.delete(variantGid);
    this.storage.save(this.toArray());
    this.notifyChange();

    if (this.client.customerId) {
      var self = this;
      return this.client
        .fetch('/wishlist', {
          method: 'DELETE',
          body: JSON.stringify({ variant_id: variantGid }),
        })
        .catch(function (err) {
          self.cache.add(variantGid);
          self.storage.save(self.toArray());
          self.notifyChange();
          throw err;
        });
    }
    return Promise.resolve();
  };

  WishlistAPI.prototype.notifyChange = function () {
    document.dispatchEvent(
      new CustomEvent(WISHLIST_CHANGE_EVENT, { detail: { count: this.cache.size } })
    );
  };

  // ------------------------------------------------------------------
  // Core Client
  // ------------------------------------------------------------------
  function PlWishlistClient(opts) {
    opts = opts || {};
    this.apiBase = opts.apiBase || '/apps/pl-wishlist';
    this.customerId = opts.customerId || null;
    this.customerEmail = opts.customerEmail || null;
    this.wishlist = new WishlistAPI(this);
    this.toast = new ToastUI();
  }

  PlWishlistClient.prototype.init = function () {
    var self = this;
    // init（ローカル即時反映＋ログイン顧客はリモート取得）を待ってからマージを試みる。
    // マージ自体は内部で非同期 fetch を投げるだけで UI フローをブロックしない。
    return this.wishlist.init().then(function () {
      if (self.customerId) {
        // 戻り値の Promise は待たない（バックグラウンド実行・UI 非ブロック）。
        self.wishlist.maybeMergeFromLocalStorage();
      }
    });
  };

  PlWishlistClient.prototype.fetch = function (path, opts) {
    opts = opts || {};
    var headers = Object.assign({ 'Content-Type': 'application/json' }, opts.headers || {});
    return fetch(this.apiBase + path, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body,
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (errBody) {
            var e = new Error(errBody.error || 'API error: ' + res.status);
            e.code = errBody.code;
            e.status = res.status;
            throw e;
          });
      }
      return res.json().catch(function () {
        return {};
      });
    });
  };

  // ------------------------------------------------------------------
  // Web Component: <pl-wishlist-button>
  // ------------------------------------------------------------------
  function defineButton() {
    if (!window.customElements || customElements.get('pl-wishlist-button')) return;

    class PlWishlistButton extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.mode || 'card'; // 'card' | 'pdp'
        this.variantGid = this.dataset.variantGid || null;

        this.setAttribute('role', 'button');
        if (!this.hasAttribute('tabindex') && !this.isDisabled()) {
          this.setAttribute('tabindex', '0');
        }

        this._onClick = this.onClick.bind(this);
        this._onKey = this.onKeydown.bind(this);
        this._onWishlistChange = this.updateState.bind(this);

        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKey);
        document.addEventListener(WISHLIST_CHANGE_EVENT, this._onWishlistChange);

        this.updateState();

        if (this.mode === 'pdp') {
          this.observeVariantChange();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKey);
        document.removeEventListener(WISHLIST_CHANGE_EVENT, this._onWishlistChange);
        if (this._variantPoll) clearInterval(this._variantPoll);
        if (this._variantSelectsEl && this._onVariantSelectChange) {
          this._variantSelectsEl.removeEventListener('change', this._onVariantSelectChange);
        }
        if (this._mainForm && this._onVariantSelectChange) {
          this._mainForm.removeEventListener('change', this._onVariantSelectChange);
        }
      }

      isDisabled() {
        return this.hasAttribute('disabled') || this.getAttribute('aria-disabled') === 'true';
      }

      onKeydown(e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.onClick(e);
        }
      }

      onClick(e) {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        if (this.isDisabled() || !this.variantGid) return;
        if (!window.plWishlist) return;

        var api = window.plWishlist.wishlist;
        var toast = window.plWishlist.toast;
        var wasActive = api.has(this.variantGid);
        var self = this;

        // --- 楽観的更新: バックエンド応答を待たず即座に見た目を切替（< 100ms）---
        this.setActive(!wasActive);
        this.classList.add('pl-wishlist-btn--loading');

        var op = wasActive ? api.remove(this.variantGid) : api.add(this.variantGid);

        op.then(function () {
          if (wasActive) {
            toast.show('お気に入りから削除しました');
          } else if (self.shouldOfferRestockOptin()) {
            // Sprint8 X1（F-401・案A）: P1（在庫切れ×非生産終了×未購読）の PDP 追加時だけ、
            // 追加成功トーストを「持続トースト＋同時購読チェック（既定ON）」にする。
            self.offerRestockOptin();
          } else {
            toast.show('お気に入りに追加しました');
          }
        })
          .catch(function (err) {
            // ロールバックは WishlistAPI 側で実施済み。表示も戻す。
            self.updateState();
            if (err && err.code === 'DISCONTINUED') {
              toast.show('この商品は販売を終了しました', 'error');
            } else {
              toast.show('エラーが発生しました', 'error');
            }
          })
          .then(function () {
            self.classList.remove('pl-wishlist-btn--loading');
          });
      }

      setActive(active) {
        this.dataset.active = active ? 'true' : 'false';
        this.setAttribute('aria-pressed', active ? 'true' : 'false');
        // 非活性（廃盤×在庫なし）のときは操作不能を先に伝えるラベルを維持し、登録/削除ラベルで上書きしない（C-05）。
        if (this.isDisabled()) {
          this.setAttribute('aria-label', 'この商品は販売を終了したため、お気に入りに追加できません');
        } else {
          this.setAttribute('aria-label', active ? 'お気に入りから削除' : 'お気に入りに追加');
        }
      }

      updateState() {
        if (!window.plWishlist || !this.variantGid) {
          this.setActive(false);
          return;
        }
        this.setActive(window.plWishlist.wishlist.has(this.variantGid));
      }

      /**
       * Sprint 6（D1/D4）: 廃盤 × 在庫の出し分けをハートに適用する。
       *  - 廃盤 AND 在庫なし → 非活性（disabled aria-disabled・登録不可）。
       *  - 廃盤 AND 在庫あり → 活性のまま（残す）。
       *  - 非廃盤            → 活性。
       * 当該バリアントの廃盤/在庫は window.plRestockVariants（ベル snippet が出力）から引く。
       * 無ければ初期 Liquid の data-discontinued / data-available を使う（フォールバック）。
       */
      applyDiscontinuedInventoryState(numericId) {
        var discontinued;
        var available;
        var vdata = window.plRestockVariants || null;
        var found = null;
        if (vdata && numericId != null) {
          var idNum = parseInt(numericId, 10);
          for (var i = 0; i < vdata.length; i++) {
            if (vdata[i].id === idNum) { found = vdata[i]; break; }
          }
        }
        if (found) {
          discontinued = found.discontinued === true;
          available = found.available === true;
        } else {
          // フォールバック（初期 Liquid 値）。
          discontinued = this.dataset.discontinued === 'true';
          available = this.dataset.available === 'true';
        }

        // data-* を最新化（note 等が参照できるよう）。
        this.dataset.discontinued = discontinued ? 'true' : 'false';
        this.dataset.available = available ? 'true' : 'false';

        var shouldDisable = discontinued && !available;
        if (shouldDisable) {
          this.setAttribute('disabled', '');
          this.setAttribute('aria-disabled', 'true');
          this.removeAttribute('tabindex');
          this.setAttribute('aria-label', 'この商品は販売を終了したため、お気に入りに追加できません');
        } else {
          this.removeAttribute('disabled');
          this.removeAttribute('aria-disabled');
          if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
          // aria-label は updateState() / setActive() が登録状態に応じて整える。
        }

        // 廃盤メッセージ（.pl-discontinued-note）追従用に状態を通知する。
        // S1=廃盤×在庫あり / S2・S3=廃盤×在庫なし / ''(空)=非廃盤。
        var noteState = '';
        if (discontinued && available) noteState = 's1';
        else if (discontinued) noteState = 's2';
        document.dispatchEvent(
          new CustomEvent(VARIANT_STATE_EVENT, {
            detail: { variantGid: this.variantGid, discontinued: discontinued, available: available, noteState: noteState },
          }),
        );
      }

      /**
       * Sprint8 X1（F-401）: 同時購読提案（持続トースト＋チェック）を出すべきか判定する。
       * 出すのは P1（在庫切れ×非生産終了×未購読）かつ PDP のときのみ（spec §4.1・design-v6 X1-0）。
       *  - PDP のみ（一覧 /pages/wishlist には出さない＝F-402 と二重回避・design-v6 X1-1）。
       *  - 在庫切れ（available=false）かつ 非生産終了（discontinued=false）。判定はベルの可視条件と同一。
       *  - 既購読（このブラウザで購読済み）なら出さない（P2・重複提案しない）。
       * 在庫/生産終了は plRestockVariants（正）→無ければ data-* 属性をフォールバックに使う。
       */
      shouldOfferRestockOptin() {
        if (this.mode !== 'pdp') return false;
        if (!this.variantGid) return false;
        // 既購読（P2）には出さない。ログイン/ゲストとも「このブラウザの購読記録」で判定し、
        // 判定不能でも ON 確定時は既存購読 API の冪等で二重作成されない（spec §4.2）。
        if (isSubscribed(this.variantGid)) return false;

        var available;
        var discontinued;
        var numericId = this.variantGid.slice(VARIANT_GID_PREFIX.length);
        var vdata = window.plRestockVariants || null;
        var found = null;
        if (vdata && numericId) {
          var idNum = parseInt(numericId, 10);
          for (var i = 0; i < vdata.length; i++) {
            if (vdata[i].id === idNum) { found = vdata[i]; break; }
          }
        }
        if (found) {
          available = found.available === true;
          discontinued = found.discontinued === true;
        } else {
          available = this.dataset.available === 'true';
          discontinued = this.dataset.discontinued === 'true';
        }
        // P1 = 在庫切れ AND 非生産終了。在庫あり(P0)・生産終了(P3)では出さない。
        return available === false && discontinued === false;
      }

      /**
       * Sprint8 X1（F-401・案A）: 同時購読提案つきの持続トーストを出す。
       *  - チェック既定 ON。閉じる/確定時に checked なら購読を作成（OFF なら何もしない）。
       *  - ログイン = 登録メールで即購読 POST（既存購読 API・冪等）。
       *  - ゲスト   = 既存購読モーダル（pl-restock-modal）でメール取得→送信で購読
       *               （新規メール UI を作らない・未入力で閉じてもお気に入りは追加済み）。
       */
      offerRestockOptin() {
        var self = this;
        var toast = window.plWishlist.toast;
        toast.showRestockOptin('お気に入りに追加しました', {
          labelText: '再入荷通知も受け取る',
          defaultChecked: true,
          onConfirm: function (checked) {
            if (!checked) return; // OFF 確定: お気に入りのみ（購読しない）。
            self.subscribeRestockFromOptin();
          },
        });
      }

      /** F-401 ON 確定時の購読実行（ログイン=即 POST / ゲスト=既存モーダルへ）。 */
      subscribeRestockFromOptin() {
        var variantGid = this.variantGid;
        if (!variantGid) return;
        // 既に購読済みなら何もしない（冪等・二重作成防止）。
        if (isSubscribed(variantGid)) return;

        var client = window.plWishlist;

        // ゲスト（未ログイン）: メール取得は既存購読モーダルへ繋ぐ（新規 UI を作らない）。
        // ログイン判定は customerId（新顧客アカウント/OTP は customerEmail が null になるため）。
        if (!client.customerId) {
          if (window.plRestockModal) {
            window.plRestockModal.openFromPdpHeart(this);
          }
          // モーダルが無い/未入力で閉じても、お気に入り追加は既に成立済み（保存を妨げない）。
          return;
        }

        // ログイン: 既存購読 API（冪等）を叩く。email はあれば送り、無ければサーバーが
        //  customerId から解決する。detail は bell スニペット出力の plRestockVariants/PDP data 由来。
        var detail = this.buildRestockDetailForPdp();
        var payload = { variant_id: variantGid };
        if (client.customerEmail) payload.email = client.customerEmail;
        if (detail) payload.detail = detail;

        client
          .fetch('/restock', { method: 'POST', body: JSON.stringify(payload) })
          .then(function (res) {
            markSubscribed(variantGid);
            if (res && typeof res.subscriber_count === 'number') {
              updateBellCounts(variantGid, res.subscriber_count);
            }
            client.toast.show(
              res && res.already ? 'すでに再入荷通知に登録済みです' : '再入荷通知に登録しました',
              res && res.already ? 'info' : 'success'
            );
          })
          .catch(function (err) {
            // お気に入りは既に追加済み。購読のみ失敗＝エラートーストで知らせる（握り潰さない）。
            console.warn('[pl-wishlist] F-401 subscribe failed:', err);
            if (err && err.code === 'DISCONTINUED') {
              client.toast.show('この商品は販売を終了しました', 'error');
            } else {
              client.toast.show('再入荷通知の登録に失敗しました', 'error');
            }
          });
      }

      /**
       * PDP のハート（＝同 PDP のベル snippet が plRestockVariants を出力）から、購読 POST 用の
       * 商品/バリアント詳細スナップショットを組む。ベルの data-* と同形式（Admin 再解決を省く）。
       * 値が取れない場合は null フィールドを含めない（バックエンドが Admin 解決にフォールバック）。
       */
      buildRestockDetailForPdp() {
        var detail = {};
        var productGid = this.dataset.productGid;
        if (productGid) detail.product_id = productGid;

        // 同 PDP のベルが出力する plRestockVariants から現バリアントの title/price/image を引く。
        var numericId = this.variantGid ? this.variantGid.slice(VARIANT_GID_PREFIX.length) : null;
        var vdata = window.plRestockVariants || null;
        if (vdata && numericId) {
          var idNum = parseInt(numericId, 10);
          for (var i = 0; i < vdata.length; i++) {
            if (vdata[i].id === idNum) {
              var v = vdata[i];
              if (v.title != null) detail.variant_title = v.title;
              if (v.image != null) detail.image_url = v.image;
              if (v.price != null && !isNaN(parseInt(v.price, 10))) {
                detail.price_minor = parseInt(v.price, 10);
              }
              break;
            }
          }
        }
        // ベル（同 PDP に常設・購読 detail の出力元）から商品ハンドル/タイトルを補う。
        var bell = document.querySelector('pl-restock-bell');
        if (bell) {
          var handle = bell.getAttribute('data-product-handle');
          var pTitle = bell.getAttribute('data-product-title');
          if (handle && !detail.handle) detail.handle = handle;
          if (pTitle && !detail.product_title) detail.product_title = pTitle;
        }
        return detail;
      }

      // PDP モード: 選択中バリアントの変化に追従（スティッキーカートと同じ手法）
      observeVariantChange() {
        var self = this;
        var mainForm = document.querySelector('product-form form, form[action*="/cart/add"]');
        var mainVariantInput = mainForm ? mainForm.querySelector('input[name="id"]') : null;
        this._mainForm = mainForm;

        var resolveCurrentId = function () {
          if (mainVariantInput && mainVariantInput.value) {
            return VARIANT_GID_PREFIX + mainVariantInput.value;
          }
          return self.variantGid;
        };

        // 初期表示時にも廃盤×在庫の状態を確定（PDP の note/ハートを描画時点で正す）。
        self.applyDiscontinuedInventoryState(self.variantGid ? self.variantGid.slice(VARIANT_GID_PREFIX.length) : null);

        var sync = function () {
          var next = resolveCurrentId();
          if (next && next !== self.variantGid) {
            self.variantGid = next;
            self.dataset.variantGid = next;
            // data-variant-id（数値 ID）も追従させる（Sprint 1 の軽微な取りこぼし修正）。
            self.dataset.variantId = next.slice(VARIANT_GID_PREFIX.length);
            self.updateState();
            // Sprint 6: バリアント切替で廃盤×在庫を再評価（ハート活性/非活性・note 文言追従）。
            self.applyDiscontinuedInventoryState(next.slice(VARIANT_GID_PREFIX.length));
          }
        };

        var variantSelectsEl = document.querySelector('variant-selects');
        this._variantSelectsEl = variantSelectsEl;
        this._onVariantSelectChange = function () {
          setTimeout(sync, 60);
        };

        if (variantSelectsEl) {
          variantSelectsEl.addEventListener('change', this._onVariantSelectChange);
        }
        if (mainForm) {
          mainForm.addEventListener('change', this._onVariantSelectChange);
        }
        // フォールバック・ポーリング（テーマ JS が input value を遅延更新するケース対策）
        this._variantPoll = setInterval(sync, 800);
      }
    }

    customElements.define('pl-wishlist-button', PlWishlistButton);
  }

  // ------------------------------------------------------------------
  // Web Component: <pl-wishlist-badge>（ヘッダー件数バッジ / F-102）
  //   pl-wishlist:change を購読してお気に入りのバリアント数をリアルタイム表示。
  //   0 件で非表示（data-count="0" を CSS が display:none にする）。
  // ------------------------------------------------------------------
  function defineBadge() {
    if (!window.customElements || customElements.get('pl-wishlist-badge')) return;

    class PlWishlistBadge extends HTMLElement {
      connectedCallback() {
        this._countEl = this.querySelector('.pl-wishlist-badge__count') || this;
        this._onChange = this.onChange.bind(this);
        document.addEventListener(WISHLIST_CHANGE_EVENT, this._onChange);
        // 初期描画（plWishlist が既に初期化済みなら現在値、未初期化なら 0）。
        this.render(window.plWishlist ? window.plWishlist.wishlist.count() : 0);
      }

      disconnectedCallback() {
        document.removeEventListener(WISHLIST_CHANGE_EVENT, this._onChange);
      }

      onChange(e) {
        var count = e && e.detail && typeof e.detail.count === 'number' ? e.detail.count : 0;
        this.render(count);
      }

      render(count) {
        var n = count > 0 ? count : 0;
        this.dataset.count = String(n);
        // 表示は 99+ で頭打ち（バッジが横に伸びないように）。
        var label = n > 99 ? '99+' : String(n);
        if (this._countEl) this._countEl.textContent = n > 0 ? label : '';
        this.setAttribute('aria-hidden', n > 0 ? 'false' : 'true');
        // スクリーンリーダー向けラベル（件数つき）。
        this.setAttribute(
          'aria-label',
          n > 0 ? 'お気に入り ' + n + ' 件' : 'お気に入りはありません'
        );
      }
    }

    customElements.define('pl-wishlist-badge', PlWishlistBadge);
  }

  // ------------------------------------------------------------------
  // 共通: カート追加（/cart/add.js）— スティッキーカートと同じセクション差し替え手法
  // ------------------------------------------------------------------
  function addVariantToCart(variantNumericId, quantity) {
    var qty = Math.max(1, parseInt(quantity, 10) || 1);

    // 差し替え対象の Dawn セクション（存在するものだけ）。
    var sectionIds = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach(function (el) {
      var sid = el.id.replace('shopify-section-', '');
      if (CART_SECTION_IDS.indexOf(sid) !== -1) sectionIds.push(sid);
    });

    var formData = new FormData();
    formData.append('id', String(variantNumericId));
    formData.append('quantity', String(qty));
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (res) {
        if (!res.ok) {
          return res
            .json()
            .catch(function () {
              return {};
            })
            .then(function (err) {
              throw new Error(err.description || 'カート追加に失敗しました');
            });
        }
        return res.json();
      })
      .then(function (data) {
        // 返ってきたセクション HTML でヘッダーのカート件数等を差し替える。
        if (data && data.sections) {
          Object.keys(data.sections).forEach(function (sectionId) {
            var target = document.getElementById('shopify-section-' + sectionId);
            if (!target) return;
            var doc = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
            var fresh = doc.getElementById('shopify-section-' + sectionId);
            if (fresh) target.innerHTML = fresh.innerHTML;
          });
        }

        // Dawn の cartUpdate を publish（他コンポーネントの同期）。
        if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'pl-wishlist-list',
              productVariantId: String(variantNumericId),
              cartData: data,
            });
          } catch (e) {
            // publish 失敗は致命的でない。
          }
        }

        // カートドロワー/通知を開く（あれば）。
        var cartDrawer = document.querySelector('cart-drawer');
        var cartNotif = document.querySelector('cart-notification');
        if (cartNotif && typeof cartNotif.open === 'function') {
          cartNotif.open();
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

        return data;
      });
  }

  // ------------------------------------------------------------------
  // 共通: 価格フォーマット（ゲスト解決経路用。整数円 → 表示文字列）
  //   Shopify.formatMoney があれば money_format を尊重、無ければ ¥ 区切り表示。
  // ------------------------------------------------------------------
  function formatMoney(amountYen) {
    var cents = Math.round(amountYen) * 100; // JPY は通常小数なしだが formatMoney は cents 前提。
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        var fmt = (window.plWishlistConfig && window.plWishlistConfig.moneyFormat) || '¥{{amount_no_decimals}}';
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) {
        // フォールバックへ。
      }
    }
    return '¥' + Math.round(amountYen).toLocaleString('ja-JP');
  }

  // ------------------------------------------------------------------
  // お気に入り一覧ページ（F-103）
  //   - source="server": ログイン顧客。Liquid が描画済み。JS はイベント委譲 + 件数同期のみ。
  //   - source="local" : ゲスト。LocalStorage の variant GID を /resolve で解決し、
  //                       Liquid と同一の DOM を生成して描画する。
  //   削除/カート投入/再入荷ボタンは listEl への 1 系統のイベント委譲で処理（両経路共通）。
  // ------------------------------------------------------------------
  function WishlistListPage(client, pageEl) {
    this.client = client;
    this.pageEl = pageEl;
    this.listEl = pageEl.querySelector('[data-pl-wishlist-list]');
    this.emptyEl = pageEl.querySelector('[data-pl-wishlist-empty]');
    this.loadingEl = pageEl.querySelector('[data-pl-wishlist-loading]');
    this.countEl = pageEl.querySelector('[data-pl-wishlist-count]');
    this.source = this.listEl ? this.listEl.getAttribute('data-source') : 'local';
    // Sprint 7: 生産終了行の 60 日カウントダウン起点をローカル保持する（ログイン/ゲスト共通）。
    this.discMeta = new DiscontinuedMetaStore();
  }

  WishlistListPage.prototype.init = function () {
    if (!this.listEl) return;

    // イベント委譲（両経路共通）。
    this._onListClick = this.onListClick.bind(this);
    this.listEl.addEventListener('click', this._onListClick);

    // 件数バッジ等と同じ pl-wishlist:change を購読し、件数表示を同期する。
    this._onChange = this.onWishlistChange.bind(this);
    document.addEventListener(WISHLIST_CHANGE_EVENT, this._onChange);

    if (this.source === 'server') {
      // ログイン: Liquid が描画済み。件数とエンプティを現在の DOM から確定。
      this.refreshCountFromDom();
      this.syncEmptyState();
      // Sprint 7: Liquid が出した生産終了行のカウントダウンを算出して埋める（描画時1回・新規 fetch なし）。
      this.applyCountdowns();
    } else {
      // ゲスト: LocalStorage を /resolve で解決して描画。
      this.renderGuest();
    }
  };

  /**
   * 一覧内の生産終了行（[data-pl-wl-countdown]）の残日数・段階を算出して埋める（Sprint 7・F-303b）。
   * 起点（discontinued_at）はローカル保持（DiscontinuedMetaStore）の「初認識日」を使う。
   * 描画時に 1 回だけ算出する（新規 fetch・ポーリングを足さない・design-v5 §W1-3）。
   * ログイン（Liquid SSR）・ゲスト（buildRow）どちらの行にも同じ処理を当て、見え方を揃える。
   */
  WishlistListPage.prototype.applyCountdowns = function () {
    if (!this.listEl) return;
    var rows = this.listEl.querySelectorAll('.pl-wl-row--discontinued[data-variant-id]');
    var activeGids = new Set();
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var variantGid = row.getAttribute('data-variant-id');
      var el = row.querySelector('[data-pl-wl-countdown]');
      if (!variantGid || !el) continue;
      activeGids.add(variantGid);
      // 起点を取得（未記録ならこのブラウザの初認識日として記録）。
      var startIso = this.discMeta.getOrInit(variantGid);
      var cd = computeCountdown(startIso);
      el.setAttribute('data-countdown-stage', cd.stage);
      el.textContent = cd.text;
    }
    // 生産終了でなくなった/消えたバリアントの起点を掃除（持ち越さない・復活/削除対応）。
    this.discMeta.retainOnly(activeGids);
  };

  /**
   * ゲスト経路: LocalStorage の variant GID を解決して一覧を描画する。
   *
   * ★パフォーマンス設計（Sprint 3 修正）:
   *   /resolve は App Proxy→Vercel→Admin GraphQL の往復でウォームでも 1.2〜1.8 秒かかるため、
   *   応答を待たずに **LocalStorage の variant 件数ぶんのスケルトン行を即時描画** する
   *   （ネットワーク非依存・< 1 秒で初期表示が成立）。
   *   /resolve 到着後にスケルトンを実カードへ差し替える。スケルトンは実カードと寸法を
   *   近づけて CLS（レイアウトシフト）を最小化する。
   *   解決不能で 0 グループ（全除外/空）の場合はスケルトンを消してエンプティへフォールバック。
   */
  WishlistListPage.prototype.renderGuest = function () {
    var self = this;
    var ids = this.client.wishlist.toArray();

    if (ids.length === 0) {
      this.listEl.innerHTML = '';
      this.refreshCountFromDom();
      this.syncEmptyState();
      return;
    }

    // (A) スケルトンを即時描画（/resolve を待たない）。LS 件数ぶんのプレースホルダー行を出す。
    this.renderSkeleton(ids.length);

    // (B) /resolve は init 待ちから切り離し済み（boot で前倒し発火）。ここで即発火する。
    this.client
      .fetch('/resolve', {
        method: 'POST',
        body: JSON.stringify({ variant_ids: ids }),
      })
      .then(function (data) {
        var groups = (data && data.groups) || [];
        // スケルトンを実データで差し替え（DOM を作り切ってから innerHTML を 1 回で置換し、
        // 中間状態のちらつき・余計な reflow を抑える）。
        var frag = document.createDocumentFragment();
        groups.forEach(function (group) {
          frag.appendChild(self.buildGroup(group));
        });
        self.clearSkeleton();
        self.listEl.innerHTML = '';
        self.listEl.appendChild(frag);
        self.refreshCountFromDom();
        self.syncEmptyState();
        // Sprint 7: ゲスト経路でも生産終了行のカウントダウンを算出して埋める（ログインと見え方一致）。
        self.applyCountdowns();
        try { performance.mark('pl-wishlist:resolved'); } catch (e) {}
      })
      .catch(function (err) {
        console.warn('[pl-wishlist] resolve failed:', err);
        // 解決に失敗してもスケルトンは消す（無限スケルトンを残さない）。件数は LocalStorage 件数を
        // 表示し、エンプティは出さない（実データは取れていないがお気に入り自体は存在するため）。
        self.clearSkeleton();
        self.listEl.innerHTML = '';
        self.refreshCountFromLocal(ids.length);
        self.client.toast.show('お気に入りの読み込みに失敗しました', 'error');
      });
  };

  /**
   * スケルトン（プレースホルダー）を即時描画する。
   * 実カード（.pl-wl-group / .pl-wl-row）と同等の寸法・構造のダミーを並べ、CLS を抑える。
   * variant 件数だけ行を出すが、商品グルーピングは未解決なので「1 グループ = 1 行」を仮で並べる
   * （件数は LS の variant 数を仮表示し、resolve 到着で実際の商品数に揃える）。
   */
  WishlistListPage.prototype.renderSkeleton = function (variantCount) {
    var n = Math.max(1, Math.min(variantCount || 1, 12)); // 過剰描画を避ける上限。
    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      frag.appendChild(this.buildSkeletonCard());
    }
    this.listEl.innerHTML = '';
    this.listEl.appendChild(frag);
    this.listEl.hidden = false;
    if (this.emptyEl) this.emptyEl.hidden = true;
    // 件数は LS の variant 件数を仮表示（商品数は不明なので variant 件数と同値で仮置き→resolve で確定）。
    this.refreshCountFromLocal(variantCount);
    // 計測用マーク（User Timing API）: スケルトンが DOM に挿入された時刻。レース不要で後から
    // performance.getEntriesByName('pl-wishlist:skeleton') で初期表示時間を採取できる。
    try { performance.mark('pl-wishlist:skeleton'); } catch (e) {}
  };

  /**
   * スケルトンカード 1 枚を生成（実カードと同一の外形クラス + skeleton 修飾）。
   * 実カードと同じ `.pl-wl-group__info > ul.pl-wl-group__variants > li.pl-wl-row` の骨格を踏襲し、
   * 行も実 `.pl-wl-row`（main = 名前/価格 + actions = ボタン）と同じ flex 構造にすることで、
   * モバイル（≤749px で行が縦積み・ボタン全幅）でも実カードと高さが近くなり CLS を抑える。
   */
  WishlistListPage.prototype.buildSkeletonCard = function () {
    var article = document.createElement('article');
    article.className = 'pl-wl-group pl-wl-group--skeleton';
    article.setAttribute('data-pl-wl-skeleton', '');
    article.setAttribute('aria-hidden', 'true');

    var media = document.createElement('span');
    media.className = 'pl-wl-group__media pl-wl-skel pl-wl-skel--media';
    article.appendChild(media);

    var info = document.createElement('div');
    info.className = 'pl-wl-group__info';

    var head = document.createElement('div');
    head.className = 'pl-wl-group__head';
    var title = document.createElement('span');
    title.className = 'pl-wl-skel pl-wl-skel--title';
    head.appendChild(title);
    info.appendChild(head);

    // 実カードと同じ ul/li 構造のダミー行を 1 行（多くのカードは 1〜2 バリアント）。
    var ul = document.createElement('ul');
    ul.className = 'pl-wl-group__variants';
    var li = document.createElement('li');
    li.className = 'pl-wl-row pl-wl-row--skeleton';

    var main = document.createElement('div');
    main.className = 'pl-wl-row__main';
    var nameSkel = document.createElement('span');
    nameSkel.className = 'pl-wl-skel pl-wl-skel--name';
    main.appendChild(nameSkel);
    li.appendChild(main);

    var actions = document.createElement('div');
    actions.className = 'pl-wl-row__actions';
    var btn1 = document.createElement('span');
    btn1.className = 'pl-wl-skel pl-wl-skel--btn';
    var btn2 = document.createElement('span');
    btn2.className = 'pl-wl-skel pl-wl-skel--btn';
    actions.appendChild(btn1);
    actions.appendChild(btn2);
    li.appendChild(actions);

    ul.appendChild(li);
    info.appendChild(ul);

    article.appendChild(info);
    return article;
  };

  /** スケルトンカードを全て除去する。 */
  WishlistListPage.prototype.clearSkeleton = function () {
    var skeletons = this.listEl.querySelectorAll('[data-pl-wl-skeleton]');
    for (var i = 0; i < skeletons.length; i++) {
      skeletons[i].parentNode && skeletons[i].parentNode.removeChild(skeletons[i]);
    }
  };

  /**
   * 件数表示を LocalStorage の variant 件数で仮表示する（resolve 未到着時/失敗時）。
   * 商品数は不明なため variant 件数と同値で仮置きし、resolve 到着後に refreshCountFromDom で確定する。
   */
  WishlistListPage.prototype.refreshCountFromLocal = function (variantCount) {
    var n = variantCount || 0;
    this.updateFavTabCount(n);
    if (!this.countEl) return;
    var vEl = this.countEl.querySelector('[data-pl-wl-variant-count]');
    var pEl = this.countEl.querySelector('[data-pl-wl-product-count]');
    if (vEl) vEl.textContent = String(n);
    if (pEl) pEl.textContent = String(n > 0 ? '…' : 0); // 商品数は解決後に確定（仮表示）。
    this.countEl.hidden = n === 0;
  };

  /** お気に入りタブの件数バッジ（タブラベル内）を更新する。 */
  WishlistListPage.prototype.updateFavTabCount = function (n) {
    var el = document.querySelector('[data-pl-tab-fav-count]');
    if (el) el.textContent = String(n || 0);
  };

  /**
   * /resolve のグループ 1 件から、Liquid（pl-wishlist-group.liquid）と同一構造の
   * カード要素を生成する。class / data 属性を一致させ、イベント委譲を共通化する。
   */
  WishlistListPage.prototype.buildGroup = function (group) {
    var article = document.createElement('article');
    article.className = 'pl-wl-group';
    article.setAttribute('data-pl-wl-group', '');
    article.setAttribute('data-product-id', group.product_id);

    // メディア（商品画像リンク）
    var mediaLink = document.createElement('a');
    mediaLink.className = 'pl-wl-group__media';
    mediaLink.href = group.url;
    mediaLink.setAttribute('aria-label', group.title);
    if (group.featured_image_url) {
      var img = document.createElement('img');
      img.className = 'pl-wl-group__img';
      img.src = group.featured_image_url;
      img.alt = group.title;
      img.width = 120;
      img.height = 120;
      img.loading = 'lazy';
      mediaLink.appendChild(img);
    } else {
      var ph = document.createElement('span');
      ph.className = 'pl-wl-group__img pl-wl-group__img--placeholder';
      ph.setAttribute('aria-hidden', 'true');
      mediaLink.appendChild(ph);
    }
    article.appendChild(mediaLink);

    // 情報エリア
    var info = document.createElement('div');
    info.className = 'pl-wl-group__info';

    var head = document.createElement('div');
    head.className = 'pl-wl-group__head';
    var titleLink = document.createElement('a');
    titleLink.className = 'pl-wl-group__title';
    titleLink.href = group.url;
    titleLink.textContent = group.title;
    head.appendChild(titleLink);
    info.appendChild(head);

    var ul = document.createElement('ul');
    ul.className = 'pl-wl-group__variants';
    ul.setAttribute('role', 'list');
    (group.variants || []).forEach(function (v) {
      ul.appendChild(this.buildRow(v, group));
    }, this);
    info.appendChild(ul);

    article.appendChild(info);
    return article;
  };

  /** バリアント 1 行を生成（Liquid の <li class="pl-wl-row"> と同一構造）。 */
  WishlistListPage.prototype.buildRow = function (variant, group) {
    // Sprint 7: 生産終了行（L1/L2/L3）は専用マークアップ（Liquid の生産終了分岐と DOM 一致）。
    if (variant.discontinued) {
      return this.buildDiscontinuedRow(variant, group);
    }

    var li = document.createElement('li');
    li.className = 'pl-wl-row' + (variant.available ? '' : ' pl-wl-row--oos');
    li.setAttribute('data-pl-wl-row', '');
    li.setAttribute('data-variant-id', variant.id);
    li.setAttribute('data-variant-numeric-id', variant.id.slice(VARIANT_GID_PREFIX.length));

    var main = document.createElement('div');
    main.className = 'pl-wl-row__main';
    // 単一バリアント商品の既定名「Default Title」は無意味なので出さない（商品名は見出しに表示済み）。
    if (variant.title && variant.title !== 'Default Title') {
      var name = document.createElement('span');
      name.className = 'pl-wl-row__name';
      name.textContent = variant.title;
      main.appendChild(name);
    }

    if (variant.available) {
      var price = document.createElement('span');
      price.className = 'pl-wl-row__price';
      price.textContent = formatMoney(variant.price_amount);
      main.appendChild(price);
    } else {
      var oos = document.createElement('span');
      oos.className = 'pl-wl-row__oos-label';
      oos.textContent = '在庫切れ';
      main.appendChild(oos);
    }
    li.appendChild(main);

    var actions = document.createElement('div');
    actions.className = 'pl-wl-row__actions';

    if (variant.available) {
      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'pl-wl-row__btn pl-wl-row__btn--add';
      addBtn.setAttribute('data-pl-wl-add', '');
      addBtn.textContent = 'カートに入れる';
      actions.appendChild(addBtn);
    } else {
      var notifyBtn = document.createElement('button');
      notifyBtn.type = 'button';
      notifyBtn.className = 'pl-wl-row__btn pl-wl-row__btn--notify';
      notifyBtn.setAttribute('data-pl-wl-notify', '');
      notifyBtn.setAttribute('data-subscribed', isSubscribed(variant.id) ? 'true' : 'false');
      notifyBtn.setAttribute('aria-pressed', isSubscribed(variant.id) ? 'true' : 'false');
      // ワンクリック購読（F-402・T4）に必要な variant/商品 detail を data-* で持たせる。
      // ベルの購読 POST と同じ「detail 同梱で Admin 再解決を省く」方式に揃える。
      notifyBtn.setAttribute('data-variant-gid', variant.id);
      notifyBtn.setAttribute('data-product-gid', group.product_id);
      if (group.handle) notifyBtn.setAttribute('data-product-handle', group.handle);
      if (group.title) notifyBtn.setAttribute('data-product-title', group.title);
      if (variant.title) notifyBtn.setAttribute('data-variant-title', variant.title);
      var rowImg = variant.image_url || group.featured_image_url;
      if (rowImg) notifyBtn.setAttribute('data-product-image', rowImg);
      // price_amount は円整数。バックエンドの detail.price_minor（最小単位）へは ×100 して渡す。
      if (typeof variant.price_amount === 'number') {
        notifyBtn.setAttribute('data-variant-price', String(Math.round(variant.price_amount) * 100));
      }
      if (isSubscribed(variant.id)) notifyBtn.textContent = '通知を登録済み';
      else notifyBtn.textContent = '再入荷通知に登録';
      actions.appendChild(notifyBtn);
    }

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'pl-wl-row__btn pl-wl-row__btn--remove';
    removeBtn.setAttribute('data-pl-wl-remove', '');
    removeBtn.setAttribute('aria-label', 'お気に入りから削除');
    removeBtn.textContent = '削除';
    actions.appendChild(removeBtn);

    li.appendChild(actions);
    return li;
  };

  /**
   * 生産終了バリアント 1 行を生成（Sprint 7・L1/L2/L3）。
   * Liquid（pl-wishlist-group.liquid の生産終了分岐）と **完全に同一の DOM** を出す
   * （class 名・data 属性・階層・文言を同期＝DOM 構造一致契約・F-303f）。
   *  - L1（在庫あり）= 「生産終了・在庫限り」バッジ＋¥価格＋カートに入れる＋カウントダウン＋代替リンク（補助）。
   *  - L2/L3（在庫なし）= 「生産終了」バッジ＋価格/カート/再入荷なし＋カウントダウン＋代替リンク。
   *  - 再入荷導線・「在庫切れ」(danger)ラベルは出さない。
   * カウントダウンの残日数・data-countdown-stage は applyCountdowns が描画後に算出して埋める。
   */
  WishlistListPage.prototype.buildDiscontinuedRow = function (variant, group) {
    var li = document.createElement('li');
    li.className = 'pl-wl-row pl-wl-row--discontinued';
    li.setAttribute('data-pl-wl-row', '');
    li.setAttribute('data-discontinued', 'true');
    li.setAttribute('data-variant-id', variant.id);
    li.setAttribute('data-variant-numeric-id', variant.id.slice(VARIANT_GID_PREFIX.length));

    // --- main: 名前 ＋（L1 のみ ¥価格）＋ 生産終了バッジ ---
    var main = document.createElement('div');
    main.className = 'pl-wl-row__main';
    // 単一バリアント商品の既定名「Default Title」は無意味なので出さない（商品名は見出しに表示済み）。
    if (variant.title && variant.title !== 'Default Title') {
      var name = document.createElement('span');
      name.className = 'pl-wl-row__name';
      name.textContent = variant.title;
      main.appendChild(name);
    }

    var badge = document.createElement('span');
    if (variant.available) {
      // L1: 在庫限りで購入導線を残す＝¥価格を出す。
      var price = document.createElement('span');
      price.className = 'pl-wl-row__price';
      price.textContent = formatMoney(variant.price_amount);
      main.appendChild(price);
      badge.className = 'pl-wl-row__discontinued-badge pl-wl-row__discontinued-badge--limited';
      badge.textContent = '生産終了・在庫限り';
    } else {
      // L2/L3: 価格を出さない・「生産終了」バッジ（navy 地白文字）。
      badge.className = 'pl-wl-row__discontinued-badge pl-wl-row__discontinued-badge--ended';
      badge.textContent = '生産終了';
    }
    main.appendChild(badge);
    li.appendChild(main);

    // --- actions: L1 はカート＋削除 / L2・L3 は削除のみ（再入荷ボタンは出さない） ---
    var actions = document.createElement('div');
    actions.className = 'pl-wl-row__actions';
    if (variant.available) {
      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'pl-wl-row__btn pl-wl-row__btn--add';
      addBtn.setAttribute('data-pl-wl-add', '');
      addBtn.textContent = 'カートに入れる';
      actions.appendChild(addBtn);
    }
    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'pl-wl-row__btn pl-wl-row__btn--remove';
    removeBtn.setAttribute('data-pl-wl-remove', '');
    removeBtn.setAttribute('aria-label', 'お気に入りから削除');
    removeBtn.textContent = '削除';
    actions.appendChild(removeBtn);
    li.appendChild(actions);

    // --- 下段（全幅・改行）: カウントダウン（左）＋代替リンク（右） ---
    var foot = document.createElement('div');
    foot.className = 'pl-wl-row__discontinued-foot';

    // カウントダウン（自動削除予告）は「在庫なし AND 後継モデルなし」のときだけ出す（仕様変更）。
    // 在庫あり（在庫限りで買える）・後継モデルあり（後継を案内）は自動削除しないため出さない。
    // サーバ Cron（deleteExpired）・Liquid 側の出し分けと一致させる。
    if (!variant.available && variant.alternative_kind !== 'successor') {
      var countdown = document.createElement('span');
      countdown.className = 'pl-wl-row__countdown';
      countdown.setAttribute('data-pl-wl-countdown', '');
      countdown.setAttribute('data-countdown-stage', 'due');
      // role="status" は付けない（描画時1回算出の静的テキスト＝ライブリージョン不要・
      // 全行同時読み上げノイズを避ける／design-v5 §A・G-3）。Liquid 側と同期。
      // 初期テキストは :empty 罠回避＋JS 未適用でも意味が通る静かな終了文（due 文言と一致）。applyCountdowns が上書きする。
      countdown.textContent = 'まもなく一覧から削除されます';
      foot.appendChild(countdown);
    }

    // 代替リンク（後継モデル → おすすめ）。resolve が返した自社 PDP/コレクション URL（外部直リンク禁止）。
    // related の文言は4面（一覧/発生メール/リマインドメール）統一で「おすすめの商品を見る」（G-2・§4 裁定）。
    var altUrl = variant.alternative_url;
    var altLabel = variant.alternative_kind === 'successor' ? '後継モデルを見る' : 'おすすめの商品を見る';
    if (altUrl) {
      var altLink = document.createElement('a');
      altLink.className = 'pl-wl-row__alt-link';
      altLink.href = altUrl;
      altLink.appendChild(document.createTextNode(altLabel));
      var arrow = document.createElement('span');
      arrow.className = 'pl-wl-row__alt-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = ' →';
      altLink.appendChild(arrow);
      foot.appendChild(altLink);
    }

    li.appendChild(foot);
    return li;
  };

  /** リスト内クリックのイベント委譲（削除 / カート投入 / 再入荷）。 */
  WishlistListPage.prototype.onListClick = function (e) {
    var removeBtn = e.target.closest('[data-pl-wl-remove]');
    if (removeBtn) {
      e.preventDefault();
      this.handleRemove(removeBtn);
      return;
    }
    var addBtn = e.target.closest('[data-pl-wl-add]');
    if (addBtn) {
      e.preventDefault();
      this.handleAddToCart(addBtn);
      return;
    }
    var notifyBtn = e.target.closest('[data-pl-wl-notify]');
    if (notifyBtn) {
      e.preventDefault();
      this.handleNotify(notifyBtn);
      return;
    }
  };

  /**
   * F-402 一覧ワンクリック購読（T4）。
   * - ログイン顧客 = 画面遷移なし 1 クリックで購読 POST → gold ソリッド「通知を登録済み」＋トースト。
   * - ゲスト       = メールが必要なので既存購読モーダル（Sprint4 R2）を起動（新規モーダルを作らない）。
   * 重複/エラーは冪等。ポーリングはしない（クリック時のみ POST）。
   */
  WishlistListPage.prototype.handleNotify = function (btn) {
    var self = this;
    var variantGid = btn.getAttribute('data-variant-gid');
    if (!variantGid) return;

    // 既に登録済みなら何もしない（冪等・トーストのみ）。
    if (btn.getAttribute('data-subscribed') === 'true' || isSubscribed(variantGid)) {
      this.setNotifyState(btn, true);
      this.client.toast.show('すでに再入荷通知に登録済みです', 'info');
      return;
    }

    // ゲスト（未ログイン）: メール未知 → 既存モーダルを開く（同一ページ・別ページ遷移なし）。
    // ログイン判定は customerId（新顧客アカウント/OTP は customerEmail が null になるため）。
    if (!this.client.customerId) {
      if (window.plRestockModal) {
        window.plRestockModal.openFromButton(btn);
      } else {
        this.client.toast.show('登録に失敗しました。時間をおいて再度お試しください', 'error');
      }
      return;
    }

    // ログイン: 1 クリックで購読 POST。email はあれば送り、無ければサーバーが customerId から
    //  解決する。detail 同梱で Admin 再解決を省く。
    var detail = readNotifyDetail(btn);
    var payload = { variant_id: variantGid };
    if (this.client.customerEmail) payload.email = this.client.customerEmail;
    if (detail) payload.detail = detail;

    // 送信中: aria-busy・一時 disabled・文言「登録中…」（見た目は維持）。CLS は min-width で死守。
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    var prevLabel = btn.textContent;
    btn.textContent = '登録中…';

    this.client
      .fetch('/restock', { method: 'POST', body: JSON.stringify(payload) })
      .then(function (res) {
        markSubscribed(variantGid);
        self.setNotifyState(btn, true);
        if (res && res.already) {
          self.client.toast.show('すでに再入荷通知に登録済みです', 'info');
        } else {
          self.client.toast.show('再入荷通知に登録しました', 'success');
        }
      })
      .catch(function (err) {
        // 未登録へ復帰＋error トースト。
        btn.textContent = prevLabel;
        self.setNotifyState(btn, false);
        if (err && err.code === 'DISCONTINUED') {
          self.client.toast.show('この商品は販売を終了しました', 'error');
        } else {
          self.client.toast.show('登録に失敗しました。時間をおいて再度お試しください', 'error');
        }
      })
      .then(function () {
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
      });
  };

  /** notify ボタンの登録状態（data-subscribed / aria-pressed / 文言）を切替える。 */
  WishlistListPage.prototype.setNotifyState = function (btn, subscribed) {
    btn.setAttribute('data-subscribed', subscribed ? 'true' : 'false');
    btn.setAttribute('aria-pressed', subscribed ? 'true' : 'false');
    btn.textContent = subscribed ? '通知を登録済み' : '再入荷通知に登録';
  };

  WishlistListPage.prototype.getRow = function (btn) {
    return btn.closest('[data-pl-wl-row]');
  };

  /** 削除: その行を消し、お気に入りから当該 variant を除去（件数バッジも連動）。 */
  WishlistListPage.prototype.handleRemove = function (btn) {
    var row = this.getRow(btn);
    if (!row) return;
    var variantGid = row.getAttribute('data-variant-id');
    var group = row.closest('[data-pl-wl-group]');
    var self = this;

    btn.disabled = true;

    this.client.wishlist
      .remove(variantGid)
      .then(function () {
        // DOM から行を除去。グループ内が空になればカードごと除去。
        row.parentNode && row.parentNode.removeChild(row);
        if (group && !group.querySelector('[data-pl-wl-row]')) {
          group.parentNode && group.parentNode.removeChild(group);
        }
        // Sprint 7: 生産終了行を手動削除したら、ローカル保持の起点も掃除する（持ち越さない）。
        if (self.discMeta) self.discMeta.forget(variantGid);
        self.refreshCountFromDom();
        self.syncEmptyState();
        self.client.toast.show('お気に入りから削除しました');
      })
      .catch(function (err) {
        console.warn('[pl-wishlist] remove failed:', err);
        btn.disabled = false;
        self.client.toast.show('削除に失敗しました', 'error');
      });
  };

  /** カート投入: 当該 variant を /cart/add.js でカートに追加。 */
  WishlistListPage.prototype.handleAddToCart = function (btn) {
    var row = this.getRow(btn);
    if (!row) return;
    var numericId = row.getAttribute('data-variant-numeric-id');
    if (!numericId) return;
    var self = this;
    var originalLabel = btn.textContent;

    btn.disabled = true;
    btn.textContent = '追加中…';

    addVariantToCart(numericId, 1)
      .then(function () {
        btn.textContent = originalLabel;
        btn.disabled = false;
        self.client.toast.show('カートに追加しました');
      })
      .catch(function (err) {
        console.warn('[pl-wishlist] add to cart failed:', err);
        btn.textContent = originalLabel;
        btn.disabled = false;
        self.client.toast.show(err.message || 'カート追加に失敗しました', 'error');
      });
  };

  /** 現在の DOM からバリアント数・商品数を数えて件数表示を更新する。 */
  WishlistListPage.prototype.refreshCountFromDom = function () {
    var variantCount = this.listEl.querySelectorAll('[data-pl-wl-row]').length;
    var productCount = this.listEl.querySelectorAll('[data-pl-wl-group]').length;
    this.updateFavTabCount(variantCount);
    if (!this.countEl) return;
    var vEl = this.countEl.querySelector('[data-pl-wl-variant-count]');
    var pEl = this.countEl.querySelector('[data-pl-wl-product-count]');
    if (vEl) vEl.textContent = String(variantCount);
    if (pEl) pEl.textContent = String(productCount);
    this.countEl.hidden = variantCount === 0;
  };

  /**
   * 0 件ならエンプティ表示、1 件以上なら一覧表示。
   * Sprint8 X2/Pol-2: 状態確定時は必ずローディング（「読み込み中…」）を hidden にし、
   * 空状態とローディングが同時可視にならない（3状態＝empty/loading/list の排他）。
   */
  WishlistListPage.prototype.syncEmptyState = function () {
    var hasItems = this.listEl.querySelectorAll('[data-pl-wl-row]').length > 0;
    if (this.emptyEl) this.emptyEl.hidden = hasItems;
    this.listEl.hidden = !hasItems;
    // 空 or 一覧が確定したらローディングは残さない（描画前のみ可視）。
    if (this.loadingEl) this.loadingEl.hidden = true;
  };

  WishlistListPage.prototype.setLoading = function (loading) {
    if (this.loadingEl) this.loadingEl.hidden = !loading;
  };

  /**
   * pl-wishlist:change の購読。別タブ/別ページでの増減や、このページ内の操作後の
   * 件数を表示へ反映する。サーバー経路では Liquid 描画済みの DOM を信頼し、
   * ここでは件数表示の同期のみ行う（DOM 再構築はしない）。
   */
  WishlistListPage.prototype.onWishlistChange = function () {
    // スケルトン表示中（ゲストの /resolve 待ち）は DOM に実 row が無く、syncEmptyState すると
    // 誤ってエンプティに切り替わってしまうため、解決完了までは件数同期をスキップする。
    if (this.listEl.querySelector('[data-pl-wl-skeleton]')) return;
    this.refreshCountFromDom();
    this.syncEmptyState();
  };

  // ------------------------------------------------------------------
  // 再入荷通知（Restock / Sprint 4 — F-201, F-202）
  //   - <pl-restock-bell> : 在庫切れバリアントの PDP に出るベル。クリックで購読モーダルを開く。
  //   - RestockModal       : 購読モーダル（PDP に 1 つ）。ゲスト=メール入力、ログイン=1タップ。
  //   - 購読者数(R3)        : ベル直下＋モーダル内に「現在 N 人」。fetch で後挿入（CLS=0 は Liquid 側で min-height 固定）。
  //   - 購読状態の記憶       : サーバが真実だが、ゲストはメール照合できないため「このブラウザで購読した variant」を
  //                          LocalStorage(pl_restock:subscribed) に記録してベルを購読済(gold)表示にする。
  // ------------------------------------------------------------------
  var RESTOCK_STORAGE_KEY = 'pl_restock:subscribed';
  var RESTOCK_CHANGE_EVENT = 'pl-restock:change';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** このブラウザで購読済みの variant GID 集合（ベル表示用のローカル記憶）。 */
  function loadSubscribedSet() {
    try {
      var raw = localStorage.getItem(RESTOCK_STORAGE_KEY);
      if (!raw) return new Set();
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return new Set();
      return new Set(
        arr.filter(function (v) {
          return typeof v === 'string' && v.indexOf(VARIANT_GID_PREFIX) === 0;
        })
      );
    } catch (e) {
      return new Set();
    }
  }

  function saveSubscribedSet(set) {
    try {
      localStorage.setItem(RESTOCK_STORAGE_KEY, JSON.stringify(Array.from(set)));
    } catch (e) {
      console.warn('[pl-restock] localStorage save failed:', e);
    }
  }

  /** 購読済みとして記録し、ベル等へ変更を通知する。 */
  function markSubscribed(variantGid) {
    var set = loadSubscribedSet();
    if (!set.has(variantGid)) {
      set.add(variantGid);
      saveSubscribedSet(set);
    }
    document.dispatchEvent(
      new CustomEvent(RESTOCK_CHANGE_EVENT, { detail: { variantGid: variantGid } })
    );
  }

  function isSubscribed(variantGid) {
    return loadSubscribedSet().has(variantGid);
  }

  /**
   * ベル状態(localStorage)をサーバーの「アクティブ購読」(variant GID 配列)へ同期する（ログイン顧客）。
   * 再入荷が発火して notified 化／解除で非アクティブになった購読をベルから外し（＝再登録可能に戻す）、
   * 別端末で登録した購読も反映する。再入荷タブ（RestockSubsPanel）の読み込み成功時に呼ぶ。
   */
  function reconcileSubscribed(activeGids) {
    var desired = new Set();
    for (var i = 0; i < (activeGids || []).length; i++) {
      var g = activeGids[i];
      if (typeof g === 'string' && g.indexOf(VARIANT_GID_PREFIX) === 0) desired.add(g);
    }
    var current = loadSubscribedSet();
    var changed = current.size !== desired.size;
    if (!changed) {
      current.forEach(function (g) {
        if (!desired.has(g)) changed = true;
      });
    }
    if (!changed) return;
    saveSubscribedSet(desired);
    // 同一ページにベルがあれば再評価させる（PDP/一覧の購読ボタン）。
    document.dispatchEvent(new CustomEvent(RESTOCK_CHANGE_EVENT, { detail: { reconciled: true } }));
  }

  /** 購読者数 0/1/複数 の文言（design-v2 §5-2）。0 は数字を出さず能動的な誘い文句に。 */
  function restockCountText(count) {
    if (!count || count <= 0) return '最初の通知登録者になりましょう';
    return '現在 ' + count + '人 が再入荷を待っています';
  }

  // ---- 購読モーダル（PDP に 1 つ・シングルトン） ----
  function RestockModal(client, modalEl) {
    this.client = client;
    this.modal = modalEl;
    this.dialog = modalEl.querySelector('.pl-restock-modal__dialog');
    this.form = modalEl.querySelector('[data-pl-restock-form]');
    this.emailInput = modalEl.querySelector('[data-pl-restock-email]');
    this.guestField = modalEl.querySelector('[data-pl-restock-guest-field]');
    this.loggedInRow = modalEl.querySelector('[data-pl-restock-logged-in]');
    this.loggedInText = modalEl.querySelector('[data-pl-restock-logged-in-text]');
    this.submitBtn = modalEl.querySelector('[data-pl-restock-submit]');
    this.submitLabel = modalEl.querySelector('[data-pl-restock-submit-label]');
    this.errorEl = modalEl.querySelector('[data-pl-restock-error]');
    this.noteEl = modalEl.querySelector('[data-pl-restock-note]');
    this.productEl = modalEl.querySelector('[data-pl-restock-modal-product]');
    this.countEl = modalEl.querySelector('[data-pl-restock-modal-count]');
    this.countText = modalEl.querySelector('[data-pl-restock-modal-count-text]');

    this.triggerBell = null; // 閉じたらフォーカスを戻す対象。
    this.variantGid = null;

    this._bind();
  }

  RestockModal.prototype._bind = function () {
    var self = this;
    this.modal.querySelectorAll('[data-pl-restock-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.close();
      });
    });
    document.addEventListener('keydown', function (e) {
      if (self.modal.hidden) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'Tab') {
        self._trapFocus(e);
      }
    });
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self._submit();
    });
    if (this.emailInput) {
      this.emailInput.addEventListener('input', function () {
        self._clearError();
      });
    }
  };

  RestockModal.prototype.isOpen = function () {
    return !this.modal.hidden;
  };

  /** ベルから開く（PDP）。productTitle・variantTitle は見出し補助に使う。 */
  RestockModal.prototype.open = function (bellEl) {
    this._openWith(bellEl, readBellDetail(bellEl), null);
  };

  /**
   * 一覧 OOS 行の notify ボタンから開く（F-402 ゲスト経路・T4）。
   * 成功時に対象行ボタンを「登録済み（gold ソリッド）」へ反映するコールバックを渡す。
   */
  RestockModal.prototype.openFromButton = function (btn) {
    var onSuccess = function () {
      btn.setAttribute('data-subscribed', 'true');
      btn.setAttribute('aria-pressed', 'true');
      btn.textContent = '通知を登録済み';
    };
    this._openWith(btn, readNotifyDetail(btn), onSuccess);
  };

  /**
   * Sprint8 X1（F-401・ゲスト経路）: PDP のお気に入りハートからメール取得用にモーダルを開く。
   * 同時購読提案チェック ON のゲストが対象。detail はハートが組む PDP スナップショットを使う
   * （新規メール UI を作らず既存購読モーダルへ繋ぐ・design-v6 X1-1）。
   * フォーカス復帰先はハート要素。成功時はベル/一覧と同じ購読済み状態へ収束（冪等）。
   */
  RestockModal.prototype.openFromPdpHeart = function (heartEl) {
    // 見出し補助の商品名/バリアント名は同 PDP のベルから補う（ハートには無いため）。
    var bell = document.querySelector('pl-restock-bell');
    var detail = typeof heartEl.buildRestockDetailForPdp === 'function'
      ? heartEl.buildRestockDetailForPdp()
      : null;
    // _openWith は originEl の data-* から見出し（product/variant title）を読むため、
    // ベルの data-* を持つ origin としてベルを使いつつ、フォーカス復帰はハートに戻す。
    var origin = bell || heartEl;
    var self = this;
    this._openWith(origin, detail, null);
    // フォーカス復帰先をハートへ上書き（閉じたらハートに戻す）。
    this.triggerBell = heartEl;
  };

  /**
   * モーダルを開く共通処理。origin = フォーカス復帰先（ベル or 行ボタン）。
   * detail = 購読 POST に同梱する商品/バリアント詳細（Admin 再解決を省く・往復 <2 秒）。
   * onSuccess = 購読成功時の追加処理（一覧行ボタンの状態反映など・null 可）。
   */
  RestockModal.prototype._openWith = function (originEl, detail, onSuccess) {
    // cart-modal が開いていれば相互排他で閉じる（フォーカス競合・overlay 二重を避ける）。
    var cartModal = document.getElementById('pl-cart-modal');
    if (cartModal && !cartModal.hidden) {
      cartModal.hidden = true;
    }

    this.triggerBell = originEl; // 閉じたらフォーカスを戻す対象。
    this.onSuccess = onSuccess || null;
    this.variantGid = originEl.getAttribute('data-variant-gid');
    var productTitle = originEl.getAttribute('data-product-title') || '';
    var variantTitle = originEl.getAttribute('data-variant-title') || '';

    this.subscribeDetail = detail;

    // 見出し補助（商品名 / バリアント名）。価格は出さない方針（design-v2 §10）。
    // 単一バリアント商品の既定名「Default Title」は無意味なので出さない（解除確認ページと同挙動）。
    if (this.productEl) {
      this.productEl.textContent =
        variantTitle && variantTitle !== 'Default Title'
          ? productTitle + ' / ' + variantTitle
          : productTitle;
    }

    this._resetForm();
    this._renderAuthMode();
    this._loadCount();

    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';

    // 初期フォーカス: ゲスト=メール input、ログイン=送信ボタン。
    var focusTarget = this.client.customerId ? this.submitBtn : this.emailInput;
    if (focusTarget) {
      try { focusTarget.focus(); } catch (e) {}
    }
  };

  RestockModal.prototype.close = function () {
    this.modal.hidden = true;
    document.body.style.overflow = '';
    if (this.triggerBell) {
      try { this.triggerBell.focus(); } catch (e) {}
    }
  };

  /** ログイン/ゲストで入力 UI を切り替える。 */
  RestockModal.prototype._renderAuthMode = function () {
    // ログイン判定は customerId で行う（新顧客アカウント/OTP では customer.email が
    // storefront に出ず customerEmail が null になるため・email では判定しない）。
    var loggedIn = Boolean(this.client.customerId);
    var email = this.client.customerEmail;
    if (loggedIn) {
      if (this.guestField) this.guestField.hidden = true;
      if (this.loggedInRow) this.loggedInRow.hidden = false;
      if (this.loggedInText) {
        this.loggedInText.textContent = email ? email + ' に通知します' : 'ご登録のメールアドレスに通知します';
      }
      if (this.noteEl) this.noteEl.hidden = true;
    } else {
      if (this.guestField) this.guestField.hidden = false;
      if (this.loggedInRow) this.loggedInRow.hidden = true;
      if (this.noteEl) this.noteEl.hidden = false;
    }
  };

  RestockModal.prototype._resetForm = function () {
    this._clearError();
    this._setBusy(false);
    if (this.emailInput) this.emailInput.value = '';
    if (this.submitLabel) this.submitLabel.textContent = '再入荷を通知する';
  };

  RestockModal.prototype._clearError = function () {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
    if (this.emailInput) {
      this.emailInput.removeAttribute('aria-invalid');
    }
  };

  RestockModal.prototype._showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (this.emailInput) {
      this.emailInput.setAttribute('aria-invalid', 'true');
      try { this.emailInput.focus(); } catch (e) {}
    }
  };

  RestockModal.prototype._setBusy = function (busy) {
    if (this.submitBtn) {
      this.submitBtn.disabled = busy;
      this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    if (this.emailInput) this.emailInput.disabled = busy;
    if (this.submitLabel) {
      this.submitLabel.textContent = busy ? '登録中…' : '再入荷を通知する';
    }
  };

  /** モーダル内の購読者数を取得して表示（CLS=0 のため領域は Liquid で固定済み）。 */
  RestockModal.prototype._loadCount = function () {
    var self = this;
    if (!this.countText) return;
    this.countText.textContent = '';
    fetchRestockCount(this.client, this.variantGid)
      .then(function (count) {
        self.countText.textContent = restockCountText(count);
      })
      .catch(function () {
        // 取得失敗時は空のまま（領域高さは保持）。
      });
  };

  RestockModal.prototype._submit = function () {
    var self = this;
    var email = this.client.customerEmail || '';

    // ゲスト（未ログイン）: 入力メールをフォーム前バリデーション（inline）。
    // ログイン判定は customerId（新顧客アカウント/OTP は customerEmail が null になるため）。
    if (!this.client.customerId) {
      var entered = this.emailInput ? this.emailInput.value.trim() : '';
      if (!EMAIL_RE.test(entered)) {
        this._showError('メールアドレスの形式が正しくありません');
        return;
      }
      email = entered;
    }
    // ログイン: email が取れていれば送る。取れない場合は空で送り、サーバーが
    //  customerId（App Proxy 署名）から登録メールを解決する。

    this._clearError();
    this._setBusy(true);

    var payload = { variant_id: this.variantGid };
    if (email) payload.email = email;
    // 商品/バリアント詳細を同梱（バックエンドが Admin 再解決を省いて即 INSERT → 往復 <2 秒）。
    if (this.subscribeDetail) payload.detail = this.subscribeDetail;

    this.client
      .fetch('/restock', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      .then(function (res) {
        var count = res && typeof res.subscriber_count === 'number' ? res.subscriber_count : null;
        markSubscribed(self.variantGid);
        if (count !== null) updateBellCounts(self.variantGid, count);

        // 一覧経路（F-402 ゲスト）: 対象行ボタンを登録済みへ反映する。
        if (typeof self.onSuccess === 'function') {
          try { self.onSuccess(); } catch (e) {}
        }

        self.close();
        if (res && res.already) {
          self.client.toast.show('すでに再入荷通知に登録済みです', 'info');
        } else {
          self.client.toast.show('再入荷通知に登録しました', 'success');
        }
      })
      .catch(function (err) {
        self._setBusy(false);
        if (err && err.code === 'DISCONTINUED') {
          self.close();
          self.client.toast.show('この商品は販売を終了しました', 'error');
        } else if (err && err.code === 'VALIDATION_ERROR') {
          self._showError('メールアドレスの形式が正しくありません');
        } else {
          self.client.toast.show('登録に失敗しました。時間をおいて再度お試しください', 'error');
        }
      });
  };

  /** モーダル内フォーカストラップ（Tab/Shift+Tab が dialog 内で循環）。 */
  RestockModal.prototype._trapFocus = function (e) {
    var focusables = this.dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var visible = Array.prototype.filter.call(focusables, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (visible.length === 0) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first || !this.dialog.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  /**
   * ベルの data-* 属性から購読 POST 用の商品/バリアント詳細スナップショットを読む。
   * バックエンドはこれを使って Admin API 再解決を回避し即 INSERT する（往復短縮）。
   * 値が欠けている/不正な属性は含めない（バックエンドはその分だけ Admin 解決にフォールバック）。
   */
  function readBellDetail(bellEl) {
    if (!bellEl) return null;
    var detail = {};
    var productGid = bellEl.getAttribute('data-product-gid');
    var handle = bellEl.getAttribute('data-product-handle');
    var productTitle = bellEl.getAttribute('data-product-title');
    var variantTitle = bellEl.getAttribute('data-variant-title');
    var image = bellEl.getAttribute('data-product-image');
    var priceRaw = bellEl.getAttribute('data-variant-price');

    if (productGid) detail.product_id = productGid;
    if (handle) detail.handle = handle;
    if (productTitle) detail.product_title = productTitle;
    if (variantTitle) detail.variant_title = variantTitle;
    if (image) detail.image_url = image;
    // Liquid の variant.price は最小通貨単位（JPY は ×100 された整数）。バックエンドの
    // priceCents（円整数）に合わせるため最小単位の整数として渡し、変換はサーバ側で行う。
    if (priceRaw !== null && priceRaw !== '') {
      var priceMinor = parseInt(priceRaw, 10);
      if (!isNaN(priceMinor) && priceMinor >= 0) detail.price_minor = priceMinor;
    }
    return detail;
  }

  /**
   * 一覧 OOS 行の notify ボタン（F-402）の data-* から購読 POST 用の詳細スナップショットを読む。
   * ベルの readBellDetail と同形式（バックエンドが Admin 再解決を省いて即 INSERT する）。
   */
  function readNotifyDetail(btn) {
    if (!btn) return null;
    var detail = {};
    var productGid = btn.getAttribute('data-product-gid');
    var handle = btn.getAttribute('data-product-handle');
    var productTitle = btn.getAttribute('data-product-title');
    var variantTitle = btn.getAttribute('data-variant-title');
    var image = btn.getAttribute('data-product-image');
    var priceRaw = btn.getAttribute('data-variant-price');

    if (productGid) detail.product_id = productGid;
    if (handle) detail.handle = handle;
    if (productTitle) detail.product_title = productTitle;
    if (variantTitle) detail.variant_title = variantTitle;
    if (image) detail.image_url = image;
    if (priceRaw !== null && priceRaw !== '') {
      var priceMinor = parseInt(priceRaw, 10);
      if (!isNaN(priceMinor) && priceMinor >= 0) detail.price_minor = priceMinor;
    }
    return detail;
  }

  /** 購読者数を fetch（GET /restock?op=count&variant_id=...）。失敗時は reject。 */
  function fetchRestockCount(client, variantGid) {
    var url = '/restock?op=count&variant_id=' + encodeURIComponent(variantGid);
    return client.fetch(url, { method: 'GET' }).then(function (res) {
      return res && typeof res.count === 'number' ? res.count : 0;
    });
  }

  /** PDP のベル直下・モーダル内の購読者数表示を、指定 variant について更新する。 */
  function updateBellCounts(variantGid, count) {
    document.querySelectorAll('[data-pl-restock-count]').forEach(function (el) {
      if (el.getAttribute('data-variant-gid') !== variantGid) return;
      var textEl = el.querySelector('[data-pl-restock-count-text]');
      if (textEl) textEl.textContent = restockCountText(count);
    });
  }

  /** <pl-restock-bell> カスタム要素。 */
  function defineBell() {
    if (!window.customElements || customElements.get('pl-restock-bell')) return;

    class PlRestockBell extends HTMLElement {
      connectedCallback() {
        this.variantGid = this.dataset.variantGid || null;
        this.setAttribute('role', 'button');
        if (!this.hasAttribute('tabindex') && !this.isDisabled()) {
          this.setAttribute('tabindex', '0');
        }

        this._onClick = this.onClick.bind(this);
        this._onKey = this.onKeydown.bind(this);
        this._onRestockChange = this.updateState.bind(this);

        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKey);
        document.addEventListener(RESTOCK_CHANGE_EVENT, this._onRestockChange);

        this.updateState();
        this.refreshCount();
        this.observeVariantChange();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKey);
        document.removeEventListener(RESTOCK_CHANGE_EVENT, this._onRestockChange);
        if (this._variantPoll) clearInterval(this._variantPoll);
        if (this._variantSelectsEl && this._onVariantSelectChange) {
          this._variantSelectsEl.removeEventListener('change', this._onVariantSelectChange);
        }
        if (this._mainForm && this._onVariantSelectChange) {
          this._mainForm.removeEventListener('change', this._onVariantSelectChange);
        }
      }

      isDisabled() {
        return this.hasAttribute('disabled') || this.getAttribute('aria-disabled') === 'true';
      }

      /** ベル直下の購読者数要素（同じ variant の data-variant-gid で対応づく）。 */
      getCountEl() {
        var els = document.querySelectorAll('[data-pl-restock-count]');
        for (var i = 0; i < els.length; i++) {
          if (els[i].getAttribute('data-variant-gid') === this.variantGid) return els[i];
        }
        return null;
      }

      onKeydown(e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.onClick(e);
        }
      }

      onClick(e) {
        if (e && e.preventDefault) e.preventDefault();
        if (this.isDisabled() || !this.variantGid) return;
        if (!window.plWishlist) return;

        if (isSubscribed(this.variantGid)) {
          // 購読済はモーダルを開かずトーストのみ（解除は Sprint 5）。
          window.plWishlist.toast.show('すでに再入荷通知に登録済みです');
          return;
        }
        if (window.plRestockModal) {
          window.plRestockModal.open(this);
        }
      }

      setSubscribed(subscribed) {
        this.dataset.subscribed = subscribed ? 'true' : 'false';
        this.setAttribute('aria-pressed', subscribed ? 'true' : 'false');
        this.setAttribute('aria-label', subscribed ? '再入荷通知 登録済み' : '再入荷通知に登録');
      }

      updateState() {
        if (!this.variantGid) {
          this.setSubscribed(false);
          return;
        }
        this.setSubscribed(isSubscribed(this.variantGid));
      }

      /** 購読者数を取得してベル直下に表示。 */
      refreshCount() {
        if (!window.plWishlist || !this.variantGid || this.hidden) return;
        var self = this;
        var countEl = this.getCountEl();
        fetchRestockCount(window.plWishlist, this.variantGid)
          .then(function (count) {
            if (countEl) {
              var textEl = countEl.querySelector('[data-pl-restock-count-text]');
              if (textEl) textEl.textContent = restockCountText(count);
            }
          })
          .catch(function () {});
      }

      /** 在庫切れ↔在庫あり・購読状態・廃盤の追従（お気に入りハートと同じ仕組み）。 */
      observeVariantChange() {
        var self = this;
        var mainForm = document.querySelector('product-form form, form[action*="/cart/add"]');
        var mainVariantInput = mainForm ? mainForm.querySelector('input[name="id"]') : null;
        this._mainForm = mainForm;

        // 商品の全バリアント情報（在庫・廃盤）。main-product の sticky cart 同様 product.variants を使う。
        var variantsData = window.plRestockVariants || null;

        var resolveCurrentId = function () {
          if (mainVariantInput && mainVariantInput.value) {
            return mainVariantInput.value;
          }
          return self.variantGid ? self.variantGid.slice(VARIANT_GID_PREFIX.length) : null;
        };

        var findVariant = function (numericId) {
          if (!variantsData) return null;
          var idNum = parseInt(numericId, 10);
          for (var i = 0; i < variantsData.length; i++) {
            if (variantsData[i].id === idNum) return variantsData[i];
          }
          return null;
        };

        var sync = function () {
          var numericId = resolveCurrentId();
          if (!numericId) return;
          var gid = VARIANT_GID_PREFIX + numericId;
          var changed = gid !== self.variantGid;
          var variant = findVariant(numericId);
          if (changed) {
            self.variantGid = gid;
            self.dataset.variantGid = gid;
            self.dataset.variantId = numericId;
            // 購読 POST のスナップショットを正しいバリアントに保つため、
            // バリアント固有の data-* 属性（タイトル・価格・画像）も切替時に更新する。
            // これらは初期 Liquid 値のままだと切替後に古い値が POST されてしまう。
            if (variant) {
              if (variant.title != null) self.dataset.variantTitle = variant.title;
              if (variant.price != null) self.dataset.variantPrice = variant.price;
              if (variant.image != null) self.dataset.productImage = variant.image;
            }
          }

          var countEl = self.getCountEl();
          if (variant) {
            var available = variant.available;
            var discontinued = self._variantDiscontinued(variant);

            // Sprint 6（D1・AC-07）: 廃盤バリアントはベル・件数を在庫有無を問わず常に非表示にする
            //   （来ない再入荷通知を出さない）。在庫あり／在庫なしのどちらでも hidden。
            if (discontinued || available) {
              self.hidden = true;
              self.setAttribute('tabindex', '-1');
              if (countEl) {
                countEl.hidden = true;
                countEl.setAttribute('data-variant-gid', gid);
              }
            } else {
              // 在庫切れ AND 非廃盤: ベル表示（=再入荷を待てる S0'）。
              self.hidden = false;
              self.removeAttribute('disabled');
              self.removeAttribute('aria-disabled');
              self.setAttribute('tabindex', '0');
              if (countEl) {
                countEl.hidden = false;
                countEl.setAttribute('data-variant-gid', gid);
              }
              // 購読状態は LocalStorage 参照（ネットワーク不要）なので毎 tick 安全に更新できる。
              self.updateState();
              // ★購読者数(count)の fetch はバリアントが実際に変わった時のみ。
              //  800ms ポーリングは在庫/GID の DOM 追従専用。在庫切れ商品では available===false が
              //  常に真のため、ここで毎 tick fetch するとレート上限(60/分)を超えて 429 を量産する。
              //  初回取得は connectedCallback の refreshCount() で実施済み。
              if (changed) self.refreshCount();
            }
          } else if (changed) {
            // バリアント情報が無い場合でも GID 追従はする（状態・件数を更新）。
            if (countEl) countEl.setAttribute('data-variant-gid', gid);
            self.updateState();
            self.refreshCount();
          }
        };

        var variantSelectsEl = document.querySelector('variant-selects');
        this._variantSelectsEl = variantSelectsEl;
        this._onVariantSelectChange = function () {
          setTimeout(sync, 60);
        };
        if (variantSelectsEl) {
          variantSelectsEl.addEventListener('change', this._onVariantSelectChange);
        }
        if (mainForm) {
          mainForm.addEventListener('change', this._onVariantSelectChange);
        }
        this._variantPoll = setInterval(sync, 800);
      }

      _variantDiscontinued(variant) {
        // Sprint 6: plRestockVariants に各バリアントの discontinued（product OR variant の OR）を
        // 載せたので、それを正とする。無ければ初期 Liquid の data-discontinued をフォールバック。
        if (variant && typeof variant.discontinued === 'boolean') return variant.discontinued;
        return this.dataset.discontinued === 'true';
      }
    }

    customElements.define('pl-restock-bell', PlRestockBell);
  }

  /** 再入荷通知の初期化（PDP・一覧ページに購読モーダルがある）。 */
  function initRestock(client) {
    defineBell();
    var modalEl = document.getElementById('pl-restock-modal');
    if (modalEl) {
      window.plRestockModal = new RestockModal(client, modalEl);
    }
  }

  // ------------------------------------------------------------------
  // タブ（お気に入り / 再入荷通知 / T2・F-204）
  //   WAI-ARIA tabs パターン。←→キーでタブ移動。再入荷タブを初めて開いたら購読一覧を遅延ロード。
  // ------------------------------------------------------------------
  function WishlistTabs(pageEl, subsPanel) {
    this.pageEl = pageEl;
    this.subsPanel = subsPanel; // RestockSubsPanel（null 可＝ゲストで一覧を持たない場合）
    this.tablist = pageEl.querySelector('[data-pl-wishlist-tabs]');
    this.tabs = this.tablist ? Array.prototype.slice.call(this.tablist.querySelectorAll('[data-pl-tab]')) : [];
    this.panels = {
      fav: pageEl.querySelector('[data-pl-tab-panel="fav"]'),
      restock: pageEl.querySelector('[data-pl-tab-panel="restock"]'),
    };
    this._restockLoaded = false;
  }

  WishlistTabs.prototype.init = function () {
    if (!this.tablist || this.tabs.length === 0) return;
    var self = this;
    this.tabs.forEach(function (tab, idx) {
      tab.addEventListener('click', function () {
        self.select(tab.getAttribute('data-pl-tab'));
      });
      tab.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var next = self.tabs[(idx + dir + self.tabs.length) % self.tabs.length];
        next.focus();
        self.select(next.getAttribute('data-pl-tab'));
      });
    });
  };

  WishlistTabs.prototype.select = function (name) {
    var self = this;
    this.tabs.forEach(function (tab) {
      var active = tab.getAttribute('data-pl-tab') === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    Object.keys(this.panels).forEach(function (key) {
      var panel = self.panels[key];
      if (panel) panel.hidden = key !== name;
    });
    // 再入荷タブを初めて開いたら購読一覧を遅延ロード（初期表示の負荷を下げる）。
    if (name === 'restock' && this.subsPanel && !this._restockLoaded) {
      this._restockLoaded = true;
      this.subsPanel.load();
    }
  };

  // ------------------------------------------------------------------
  // 購読管理パネル（再入荷通知タブ / T2・F-204）
  //   GET list で購読中一覧を取得し、スケルトン先行（CLS=0）で描画。各行「解除」で DELETE。
  //   ログイン顧客専用（ゲストは Liquid 側で CTA を出す）。
  // ------------------------------------------------------------------
  function RestockSubsPanel(client, panelEl) {
    this.client = client;
    this.panelEl = panelEl;
    this.listEl = panelEl.querySelector('[data-pl-restock-subs-list]');
    this.emptyEl = panelEl.querySelector('[data-pl-restock-subs-empty]');
    this.countEl = panelEl.querySelector('[data-pl-restock-subs-count]');
    this.countNumEl = panelEl.querySelector('[data-pl-restock-subs-num]');
    this.tabCountEl = document.querySelector('[data-pl-tab-restock-count]');
    this._loaded = false;
  }

  /** ログイン顧客の購読一覧か（ゲストは list 要素自体が無い）。 */
  RestockSubsPanel.prototype.isAvailable = function () {
    return Boolean(this.listEl);
  };

  RestockSubsPanel.prototype.init = function () {
    if (!this.listEl) return;
    this._onClick = this.onListClick.bind(this);
    this.listEl.addEventListener('click', this._onClick);
  };

  /** 購読一覧をロード（タブ初回オープン時）。スケルトン先行→GET list→差し替え。 */
  RestockSubsPanel.prototype.load = function () {
    if (!this.listEl || this._loaded) return;
    this._loaded = true;
    var self = this;

    this.renderSkeleton(3);

    this.client
      .fetch('/restock', { method: 'GET' })
      .then(function (data) {
        var subs = (data && data.subscriptions) || [];
        self.render(subs);
      })
      .catch(function (err) {
        console.warn('[pl-restock] subs list failed:', err);
        // 取得失敗 ≠ 0件。スケルトンを消し、空状態は出さずトーストで知らせる。
        self.listEl.innerHTML = '';
        self.client.toast.show('購読の読み込みに失敗しました', 'error');
      });
  };

  RestockSubsPanel.prototype.render = function (subs) {
    var frag = document.createDocumentFragment();
    for (var i = 0; i < subs.length; i++) {
      frag.appendChild(this.buildSubRow(subs[i]));
    }
    this.listEl.innerHTML = '';
    this.listEl.appendChild(frag);
    this.syncState();
    // ベル状態(localStorage)をサーバーのアクティブ購読へ同期（notified/解除で消えた物を外す）。
    reconcileSubscribed(subs.map(function (s) { return s.variant_id; }));
  };

  /** スケルトン行を即時描画（GET list 待ち・CLS=0 のため実行カードと同寸法）。 */
  RestockSubsPanel.prototype.renderSkeleton = function (n) {
    var count = Math.max(1, Math.min(n || 1, 6));
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var art = document.createElement('article');
      art.className = 'pl-restock-sub pl-restock-sub--skeleton';
      art.setAttribute('data-pl-restock-sub-skeleton', '');
      art.setAttribute('aria-hidden', 'true');
      var media = document.createElement('span');
      media.className = 'pl-restock-sub__media pl-wl-skel';
      art.appendChild(media);
      var info = document.createElement('div');
      info.className = 'pl-restock-sub__info';
      var title = document.createElement('span');
      title.className = 'pl-wl-skel pl-restock-sub__skel-title';
      var line = document.createElement('span');
      line.className = 'pl-wl-skel pl-restock-sub__skel-line';
      info.appendChild(title);
      info.appendChild(line);
      art.appendChild(info);
      frag.appendChild(art);
    }
    this.listEl.innerHTML = '';
    this.listEl.appendChild(frag);
    if (this.emptyEl) this.emptyEl.hidden = true;
  };

  /**
   * 購読 1 件のカード行を生成する。
   * ★pl-restock-sub-row.liquid と DOM 構造を一致させること（class/data 属性/階層）。
   */
  RestockSubsPanel.prototype.buildSubRow = function (sub) {
    var article = document.createElement('article');
    article.className = 'pl-restock-sub';
    article.setAttribute('data-pl-restock-sub', '');
    article.setAttribute('data-sub-id', sub.id);

    // 商品ページURL（画像・商品名から飛べるように）。handle 必須・variant も付与。
    var productUrl = '';
    if (sub.handle) {
      productUrl = '/products/' + sub.handle;
      if (sub.variant_id && sub.variant_id.indexOf(VARIANT_GID_PREFIX) === 0) {
        productUrl += '?variant=' + sub.variant_id.slice(VARIANT_GID_PREFIX.length);
      }
    }

    var media = document.createElement('span');
    media.className = 'pl-restock-sub__media';
    var mediaInner;
    if (sub.image) {
      mediaInner = document.createElement('img');
      mediaInner.className = 'pl-restock-sub__img';
      mediaInner.src = sub.image;
      mediaInner.alt = sub.product_title || '';
      mediaInner.width = 96;
      mediaInner.height = 96;
      mediaInner.loading = 'lazy';
    } else {
      mediaInner = document.createElement('span');
      mediaInner.className = 'pl-restock-sub__img pl-restock-sub__img--placeholder';
      mediaInner.setAttribute('aria-hidden', 'true');
    }
    if (productUrl) {
      var mediaLink = document.createElement('a');
      mediaLink.className = 'pl-restock-sub__media-link';
      mediaLink.href = productUrl;
      mediaLink.setAttribute('aria-label', sub.product_title || '商品ページを見る');
      mediaLink.appendChild(mediaInner);
      media.appendChild(mediaLink);
    } else {
      media.appendChild(mediaInner);
    }
    article.appendChild(media);

    var info = document.createElement('div');
    info.className = 'pl-restock-sub__info';

    var title = document.createElement('p');
    title.className = 'pl-restock-sub__title';
    if (productUrl) {
      var titleLink = document.createElement('a');
      titleLink.className = 'pl-restock-sub__title-link';
      titleLink.href = productUrl;
      titleLink.textContent = sub.product_title || '';
      title.appendChild(titleLink);
    } else {
      title.textContent = sub.product_title || '';
    }
    info.appendChild(title);

    var vTitle = sub.variant_title;
    if (vTitle && vTitle !== 'Default Title') {
      var variant = document.createElement('p');
      variant.className = 'pl-restock-sub__variant';
      variant.textContent = vTitle;
      info.appendChild(variant);
    }

    var foot = document.createElement('div');
    foot.className = 'pl-restock-sub__foot';
    var date = document.createElement('span');
    date.className = 'pl-restock-sub__date';
    date.textContent = '登録日 ' + formatSubDate(sub.subscribed_at);
    foot.appendChild(date);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pl-restock-sub__btn pl-restock-sub__btn--cancel';
    btn.setAttribute('data-pl-restock-unsub', '');
    btn.setAttribute('aria-label', (sub.product_title || '') + ' の再入荷通知を解除');
    btn.textContent = '解除';
    foot.appendChild(btn);

    info.appendChild(foot);
    article.appendChild(info);
    return article;
  };

  RestockSubsPanel.prototype.onListClick = function (e) {
    var btn = e.target.closest('[data-pl-restock-unsub]');
    if (!btn) return;
    e.preventDefault();
    this.handleUnsub(btn);
  };

  /** 行の「解除」: DELETE /restock/{id} → 行をフェードアウト除去・件数 -1・トースト。 */
  RestockSubsPanel.prototype.handleUnsub = function (btn) {
    var self = this;
    var row = btn.closest('[data-pl-restock-sub]');
    if (!row) return;
    var id = row.getAttribute('data-sub-id');
    if (!id) return;

    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    var prev = btn.textContent;
    btn.textContent = '解除中…';

    this.client
      .fetch('/restock/' + encodeURIComponent(id), { method: 'DELETE' })
      .then(function () {
        row.parentNode && row.parentNode.removeChild(row);
        self.syncState();
        self.client.toast.show('再入荷通知を解除しました', 'success');
      })
      .catch(function (err) {
        console.warn('[pl-restock] unsubscribe failed:', err);
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        btn.textContent = prev;
        self.client.toast.show('解除に失敗しました', 'error');
      });
  };

  /** 件数行・空状態・タブ件数を現在の DOM から同期する。 */
  RestockSubsPanel.prototype.syncState = function () {
    var n = this.listEl.querySelectorAll('[data-pl-restock-sub]').length;
    if (this.countNumEl) this.countNumEl.textContent = String(n);
    if (this.countEl) this.countEl.hidden = n === 0;
    if (this.emptyEl) this.emptyEl.hidden = n > 0;
    if (this.tabCountEl) this.tabCountEl.textContent = String(n);
  };

  /** subscribed_at（ISO）を YYYY/MM/DD へ。パース不能はそのまま返す。 */
  function formatSubDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    var y = d.getFullYear();
    var m = ('0' + (d.getMonth() + 1)).slice(-2);
    var day = ('0' + d.getDate()).slice(-2);
    return y + '/' + m + '/' + day;
  }

  // ------------------------------------------------------------------
  // 購読解除確認ページ（/pages/restock-unsubscribe / T3・F-205）
  //   token を読み GET（確認・解除しない）→ 明示クリックで POST（解除）。ログイン不要。
  //   2 段階: メールクライアントのリンクプリフェッチで誤解除されない（GET では解除しない）。
  // ------------------------------------------------------------------
  function RestockUnsubscribePage(client, pageEl) {
    this.client = client;
    this.pageEl = pageEl;
    this.confirmBtn = pageEl.querySelector('[data-pl-unsub-confirm]');
    this.confirmLabel = pageEl.querySelector('[data-pl-unsub-confirm-label]');
    this.productEl = pageEl.querySelector('[data-pl-unsub-product]');
    this.liveEl = pageEl.querySelector('[data-pl-unsub-live]');
    this.token = getQueryParam('token');
  }

  RestockUnsubscribePage.prototype.init = function () {
    var self = this;
    if (!this.token) {
      this.setState('invalid');
      return;
    }
    if (this.confirmBtn) {
      this.confirmBtn.addEventListener('click', function () {
        self.doUnsubscribe();
      });
    }
    // GET は「確認用」: 解除はしない（2 段階・プリフェッチ誤解除防止）。
    this.client
      .fetch('/restock/unsubscribe?token=' + encodeURIComponent(this.token), { method: 'GET' })
      .then(function (data) {
        var state = (data && data.state) || 'invalid';
        if (state === 'confirm') {
          self.renderProduct(data);
          self.setState('confirm');
        } else if (state === 'already') {
          self.setState('already');
        } else {
          self.setState('invalid');
        }
      })
      .catch(function (err) {
        console.warn('[pl-restock] unsubscribe lookup failed:', err);
        self.setState('invalid');
      });
  };

  RestockUnsubscribePage.prototype.renderProduct = function (data) {
    if (!this.productEl) return;
    var p = (data && data.product_title) || '';
    var v = (data && data.variant_title) || '';
    if (!p) return;
    this.productEl.textContent = v && v !== 'Default Title' ? p + ' / ' + v : p;
    this.productEl.hidden = false;
  };

  /** 明示クリックで解除（POST）。冪等。 */
  RestockUnsubscribePage.prototype.doUnsubscribe = function () {
    var self = this;
    if (!this.token) {
      this.setState('invalid');
      return;
    }
    if (this.confirmBtn) {
      this.confirmBtn.disabled = true;
      this.confirmBtn.setAttribute('aria-busy', 'true');
    }
    if (this.confirmLabel) this.confirmLabel.textContent = '解除中…';

    this.client
      .fetch('/restock/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ token: this.token }),
      })
      .then(function (data) {
        var state = data && data.state === 'already' ? 'already' : 'done';
        self.setState(state);
      })
      .catch(function (err) {
        console.warn('[pl-restock] unsubscribe failed:', err);
        // 失敗時は確認状態へ戻し再試行可能に。
        if (self.confirmBtn) {
          self.confirmBtn.disabled = false;
          self.confirmBtn.removeAttribute('aria-busy');
        }
        if (self.confirmLabel) self.confirmLabel.textContent = '購読を解除する';
        self.client.toast.show('解除に失敗しました。時間をおいて再度お試しください', 'error');
      });
  };

  /** data-pl-unsub-state ブロックの表示を切替える（hidden 属性で出し分け）。 */
  RestockUnsubscribePage.prototype.setState = function (state) {
    var blocks = this.pageEl.querySelectorAll('[data-pl-unsub-state]');
    for (var i = 0; i < blocks.length; i++) {
      blocks[i].hidden = blocks[i].getAttribute('data-pl-unsub-state') !== state;
    }
    if (this.liveEl) {
      var msg =
        state === 'done' ? '購読を解除しました' :
        state === 'already' ? 'この通知はすでに解除されています' :
        state === 'invalid' ? 'リンクが無効です' : '';
      if (msg) this.liveEl.textContent = msg;
    }
  };

  /** URL クエリパラメータを読む。 */
  function getQueryParam(name) {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get(name);
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------------------------
  // Bootstrap
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // 廃盤管理: PDP 緊急性/終了メッセージ（.pl-discontinued-note / Sprint 6・D1）
  //   ハート（PlWishlistButton.applyDiscontinuedInventoryState）が発火する
  //   pl-wishlist:variant-state を購読し、廃盤×在庫の状態に応じて data-state と hidden を更新する。
  //   新規 fetch ループは作らない（ハートの observeVariantChange に相乗り）。
  // ------------------------------------------------------------------
  function initDiscontinuedNote() {
    var note = document.querySelector('[data-pl-discontinued-note]');
    if (!note) return;
    document.addEventListener(VARIANT_STATE_EVENT, function (e) {
      var detail = (e && e.detail) || {};
      var state = detail.noteState || '';
      if (state === 's1' || state === 's2') {
        note.setAttribute('data-state', state);
        note.hidden = false;
      } else {
        // 非廃盤バリアントに切替えたら note を消す。
        note.setAttribute('data-state', '');
        note.hidden = true;
      }
    });
  }

  // ------------------------------------------------------------------
  // LINE 連携導線＋通知チャネル設定（Phase 2 / Sprint L2・C1/C2/F-505a）
  //
  //   ★ログイン顧客のみ★（snippet 自体が customer 判定で render・ゲストは要素なし）。
  //   GET /apps/pl-wishlist/line（L1）で実状態を取得し、未連携/連携済を出し分ける。
  //   - 連携トリガーは <a href=".../line/start">（App Proxy→302→LINE 認可）。click で aria-busy のみ。
  //   - C2 radio 変更 → 即 PUT（送信ボタンなし）→ 成功/失敗トースト・失敗は直前値へ戻す。
  //   - 連携を解除 → DELETE → メールへリセット＋未連携へ＋トースト。
  //   - callback 戻り（?line=linked / ?line=error）でトースト。
  //   ★ポーリングしない（イベント駆動・状態取得は init 時の 1 回のみ）★。
  // ------------------------------------------------------------------
  function LineLinkPanel(client, rootEl) {
    this.client = client;
    this.root = rootEl;
    this.radios = rootEl.querySelectorAll('[data-pl-notify-radio]');
    this.hintEl = rootEl.querySelector('[data-pl-notify-hint]');
    this.nameEl = rootEl.querySelector('[data-pl-line-name]');
    this.unlinkRow = rootEl.querySelector('[data-pl-line-unlink-row]');
    this.unlinkBtn = rootEl.querySelector('[data-pl-line-unlink]');
    this.startLink = rootEl.querySelector('[data-pl-line-start]');
    // 楽観的更新の巻き戻し用に直前の選択値を保持する。
    this.lastChannel = 'email';
  }

  var LINE_HINT_LINKED = '再入荷・生産終了のお知らせをこの方法で受け取ります。';
  var LINE_HINT_UNLINKED = '※LINE・両方を選ぶには、上の「LINE で通知を受け取る」から連携してください。';

  LineLinkPanel.prototype.init = function () {
    if (!this.root) return;
    var self = this;

    // 連携トリガー（<a>）: 押下で aria-busy（実遷移は href が担う・同一タブ）。
    if (this.startLink) {
      this.startLink.addEventListener('click', function () {
        self.startLink.setAttribute('aria-busy', 'true');
      });
    }

    // C2 radio 変更 → 即保存。
    for (var i = 0; i < this.radios.length; i++) {
      this.radios[i].addEventListener('change', function (e) {
        self.onChannelChange(e.target.value);
      });
    }

    // 連携を解除。
    if (this.unlinkBtn) {
      this.unlinkBtn.addEventListener('click', function () {
        self.onUnlink();
      });
    }

    // callback 戻りのトースト（?line=linked / ?line=error）。1 回だけ・URL から掃除する。
    this.handleCallbackParam();

    // 実状態の取得（init 時 1 回のみ・ポーリングなし）。
    this.loadState();
  };

  /** GET で連携状態を取得し UI に反映する（失敗時は未連携扱いで安全側）。 */
  LineLinkPanel.prototype.loadState = function () {
    var self = this;
    this.client
      .fetch('/line', { method: 'GET' })
      .then(function (data) {
        self.applyState(data || {});
      })
      .catch(function (err) {
        // 取得失敗は未連携表示で安全側に倒す（押し付けない・自動再試行しない）。
        console.warn('[pl-line] state load failed:', err);
        self.applyState({ linked: false, notify_channel: 'email' });
      });
  };

  /** 状態オブジェクト { linked, friend, notify_channel, display_name } を UI へ反映する。 */
  LineLinkPanel.prototype.applyState = function (state) {
    var linked = Boolean(state.linked);
    var channel = state.notify_channel === 'line' || state.notify_channel === 'both'
      ? state.notify_channel
      : 'email';
    this.lastChannel = channel;

    this.root.setAttribute('data-pl-line-state', linked ? 'linked' : 'unlinked');
    this.setViewVisible('unlinked', !linked);
    this.setViewVisible('linked', linked);
    if (this.unlinkRow) this.unlinkRow.hidden = !linked;

    // チャネルの活性/選択。連携済=3択活性・既定/最終選択。未連携=email 固定・LINE/両方 disabled。
    this.setRadios(linked, linked ? channel : 'email');

    if (this.hintEl) {
      this.hintEl.textContent = linked ? LINE_HINT_LINKED : LINE_HINT_UNLINKED;
    }
    if (this.nameEl) {
      this.nameEl.textContent = linked && state.display_name ? state.display_name : '';
    }
  };

  /** view（unlinked / linked）の表示/非表示。 */
  LineLinkPanel.prototype.setViewVisible = function (name, visible) {
    var el = this.root.querySelector('[data-pl-line-view="' + name + '"]');
    if (el) el.hidden = !visible;
  };

  /** radio の disabled / checked を設定する（連携済=活性・未連携=email のみ活性）。 */
  LineLinkPanel.prototype.setRadios = function (linked, selected) {
    for (var i = 0; i < this.radios.length; i++) {
      var r = this.radios[i];
      var isEmail = r.value === 'email';
      r.disabled = linked ? false : !isEmail;
      r.checked = r.value === selected;
    }
  };

  /** C2 変更 → PUT 即保存。失敗は直前値へ戻す＋error トースト（取りこぼし防止）。 */
  LineLinkPanel.prototype.onChannelChange = function (value) {
    var self = this;
    var prev = this.lastChannel;
    if (value === prev) return;
    this.lastChannel = value;

    this.client
      .fetch('/line', { method: 'PUT', body: JSON.stringify({ notify_channel: value }) })
      .then(function () {
        self.client.toast.show('通知の受け取り方を変更しました', 'success');
      })
      .catch(function (err) {
        console.warn('[pl-line] channel save failed:', err);
        // 直前値へ戻す（楽観的更新の巻き戻し）。
        self.lastChannel = prev;
        self.setRadios(true, prev);
        self.client.toast.show('設定の変更に失敗しました。時間をおいてお試しください', 'error');
      });
  };

  /** 連携を解除 → DELETE → メールへリセット＋未連携へ＋info トースト。 */
  LineLinkPanel.prototype.onUnlink = function () {
    var self = this;
    if (this.unlinkBtn) this.unlinkBtn.setAttribute('aria-busy', 'true');

    this.client
      .fetch('/line', { method: 'DELETE' })
      .then(function () {
        self.applyState({ linked: false, notify_channel: 'email' });
        self.client.toast.show('LINE 連携を解除しました', 'info');
      })
      .catch(function (err) {
        console.warn('[pl-line] unlink failed:', err);
        self.client.toast.show('連携の解除に失敗しました。時間をおいてお試しください', 'error');
      })
      .then(function () {
        if (self.unlinkBtn) self.unlinkBtn.removeAttribute('aria-busy');
      });
  };

  /** callback 戻りの ?line=linked / ?line=error をトーストし URL から掃除する（再表示防止）。 */
  LineLinkPanel.prototype.handleCallbackParam = function () {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var status = params.get('line');
    if (status !== 'linked' && status !== 'error') return;

    if (status === 'linked') {
      this.client.toast.show('LINE 連携が完了しました', 'success');
    } else {
      // 押し付けない: 失敗/キャンセルは静かに未連携のまま。再試行導線は1つ（トリガー）。
      this.client.toast.show('連携が完了しませんでした。もう一度お試しください', 'error');
    }

    // URL から line パラメータを掃除（リロード/共有での再トーストを防ぐ）。
    params.delete('line');
    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    try {
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) {
      /* replaceState 不可な環境では URL 掃除を諦める（トーストは出済み） */
    }
  };

  function boot() {
    var config = window.plWishlistConfig || {};
    var client = new PlWishlistClient({
      apiBase: config.apiBase,
      customerId: config.customerId,
      customerEmail: config.customerEmail,
    });
    window.plWishlist = client;

    defineButton();
    defineBadge();
    // 再入荷通知（Sprint 4 / F-201, F-202）。PDP のベル・購読モーダル・購読者数。
    initRestock(client);
    // 廃盤管理（Sprint 6 / D1）。PDP の緊急性/終了メッセージをバリアント切替に追従させる。
    initDiscontinuedNote();

    // お気に入り一覧ページ（F-103）。
    //
    // ★パフォーマンス設計（Sprint 3 修正）:
    //   client.init() は同期で LocalStorage を cache に読み込む（wishlist.init() 冒頭の
    //   `this.cache = new Set(this.storage.load())`）→ 返す Promise はログイン顧客の
    //   Metafields 取得・自動マージ等「重い前段」をまとめている。
    //   ゲストの一覧描画はこの重い前段を待つ必要がない（描画に必要な LS は同期で揃う）ため、
    //   一覧の init() を initPromise から切り離して即実行する＝/resolve を init 待ちから前倒し発火。
    //   さらに renderGuest() は /resolve 応答を待たずにスケルトンを即時描画する（体感初期表示を
    //   ネットワーク非依存にする）。
    var pageEl = document.querySelector('[data-pl-wishlist-page]');
    var listPage = pageEl ? new WishlistListPage(client, pageEl) : null;

    // client.init() を発火（同期部分＝LS 読込が cache に反映される）。返り Promise は待たない。
    var initPromise = client.init();

    if (listPage) {
      if (listPage.source === 'server') {
        // ログイン: Liquid 描画済み。重い前段（Metafields 統合）を反映してから件数を確定したいので
        // initPromise 後に初期化する（描画自体は既にサーバー側で完了しているため体感は即時）。
        initPromise.then(function () {
          listPage.init();
        }, function () {
          listPage.init();
        });
      } else {
        // ゲスト: LS は client.init() の同期部分で既に cache 済み。init 待ちせず即初期化し、
        // スケルトン即描画 + /resolve 並行発火する。
        listPage.init();
      }
    }

    // タブ（お気に入り / 再入荷通知 / T2）と購読管理パネル（F-204）。
    if (pageEl) {
      var subsPanelEl = pageEl.querySelector('[data-pl-tab-panel="restock"]');
      var subsPanel = null;
      if (subsPanelEl) {
        subsPanel = new RestockSubsPanel(client, subsPanelEl);
        subsPanel.init(); // list 要素が無い（ゲスト）なら no-op。
        if (subsPanel.isAvailable()) window.plRestockSubsPanel = subsPanel;
      }
      var tabs = new WishlistTabs(pageEl, subsPanel && subsPanel.isAvailable() ? subsPanel : null);
      tabs.init();

      // 再入荷タブのバッジ件数を「初期表示」で正しくするため購読一覧を先読みする。
      //   元は遅延ロード（タブ初回オープン時）だったが、それだと未オープン時にタブの数字が
      //   サーバー描画の固定値 0 のままになり実数と食い違う（一覧を開くと正しくなる）。
      //   GET /restock は App Proxy の logged_in_customer_id で解決され client.init 非依存。
      //   一覧は hidden パネル内に描画されるので視覚コスト/CLS なし。load() は冪等（再オープンで二重取得しない）。
      if (subsPanel && subsPanel.isAvailable()) {
        subsPanel.load();
      }

      // LINE 連携導線＋通知チャネル設定（Phase 2 / Sprint L2・C1/C2・ログイン顧客のみ要素あり）。
      var lineLinkEl = pageEl.querySelector('[data-pl-line-link]');
      if (lineLinkEl) {
        var lineLinkPanel = new LineLinkPanel(client, lineLinkEl);
        lineLinkPanel.init();
        window.plLineLinkPanel = lineLinkPanel;
      }
    }

    // 購読解除確認ページ（/pages/restock-unsubscribe / T3・F-205）。ログイン不要。
    var unsubEl = document.querySelector('[data-pl-restock-unsub-page]');
    if (unsubEl) {
      var unsubPage = new RestockUnsubscribePage(client, unsubEl);
      unsubPage.init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
